import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, WMSTileLayer, Polygon, Polyline, Marker, Popup, Tooltip, useMap, useMapEvents, GeoJSON, ImageOverlay } from 'react-leaflet';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Chip,
  Tabs,
  Autocomplete,
  Tab,
  Checkbox,
  Menu,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import PaletteIcon from '@mui/icons-material/Palette';
import LayersIcon from '@mui/icons-material/Layers';
import SatelliteAltIcon from '@mui/icons-material/SatelliteAlt';
import MapIcon from '@mui/icons-material/Map';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useAuth } from '../../contexts/AuthContext';
import SetDeviceLocationModal from '../SetDeviceLocationModal';
import ZoneDrawControl, { LatLngPoint, GeometryKind } from './ZoneDrawControl';
import { polygonAreaHectares } from '../../utils/geoArea';
import { bandInputInvalid, effectiveRate, formatRate, isBanded } from '../../utils/bandRate';
import OperationsPanel from './OperationsPanel';
import FieldSeasonsPanel from './FieldSeasonsPanel';
import ProductOrGroupAutocomplete from './ProductOrGroupAutocomplete';
import RecordImportDetails, { useRecordImportRows } from './RecordImportDetails';
import SatellitePanel from './SatellitePanel';
import { readOverlayAt, SatelliteImageryControls, SatelliteImageryLayer, useSatelliteOverlays } from './satelliteImagery';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import markerRetina from 'leaflet/dist/images/marker-icon-2x.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerRetina,
  shadowUrl: markerShadow,
});

interface GeoShape {
  geometryType: GeometryKind;
  points: LatLngPoint[];
}

// The collection container — the ".shp" side of a shapefile-and-attribute-
// table pair. Holds no geometry itself; see SpatialFeature for the individual
// instances. A layer's parent is always a specific feature in another layer
// (never a whole other layer directly), so the hierarchy is uniform at every
// level — see Models/SpatialLayer.cs.
export interface SpatialLayer {
  id: string;
  businessId: string;
  parentSpatialFeatureId: string | null;
  layerTypeId: string;
  layerTypeName: string;
  category: LayerCategory;
  defaultGeometryKind: GeometryKind;
  allowedGeometryKinds: GeometryKind[];
  name: string;
  colour: string | null;
  dateCreated: string;
  featureCount: number;
}

// The overlaid raster image, if this feature is an "Image Overlay" — see
// Models/SpatialImage.cs. url points at an authenticated GET action, so it's
// never used directly as an <img>/ImageOverlay src (no auth header on a
// plain browser request) — fetched as a blob and rendered via an object URL.
export interface SpatialImage {
  id: string;
  spatialFeatureId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  dateCreated: string;
  url: string;
}

// One instance within a layer — a farm, a paddock, a silo, a probe. Geometry
// is nullable (a feature can exist as a named tree entry before anyone has
// drawn its shape) and unversioned — see Models/SpatialFeature.cs.
export interface SpatialFeature {
  id: string;
  spatialLayerId: string;
  name: string;
  geometry: GeoShape | null;
  deviceId: string | null;
  deviceName: string | null;
  notes: string | null;
  colour: string | null;
  attributes: Record<string, string>;
  sourceRef: string | null;
  dateCreated: string;
  archivedDate: string | null;
  image: SpatialImage | null;
  fallbackRegionId: string | null;
  fallbackRegionName: string | null;
  fillOpacity: number | null;
  areaHectares: number | null;
}

// The three buckets the map bar creates layers under. Field's parent must be a
// Farm; Zone (a sub-field management/prescription zone — industry-standard
// placement, not a container above Farm — also used for collections of fixed
// point assets like silos/probes) may parent to a Farm or a Field. Farm is
// always top-level. Operations are not a SpatialLayer category — see
// Record/RecordItem and the "Add Operation" context-menu flow below.
export type LayerCategory = 'Farm' | 'Field' | 'Zone';

export interface SpatialLayerType {
  id: string;
  name: string;
  defaultGeometryKind: GeometryKind;
  allowedGeometryKinds: GeometryKind[];
  category: LayerCategory;
  defaultColour: string | null;
  dateCreated: string;
}

export const CATEGORY_LABELS: Record<LayerCategory, string> = {
  Zone: 'Management Zone',
  Farm: 'Farm',
  Field: 'Field (Paddock)',
};

const CATEGORY_BAR_ORDER: LayerCategory[] = ['Zone', 'Farm', 'Field'];

// Which child categories are creatable given the currently selected feature's
// layer category ('' = nothing selected, so the new layer/feature is
// top-level). Mirrors SpatialLayersController's AllowedParentCategories.
const CHILD_CATEGORIES_FOR_PARENT: Record<string, LayerCategory[]> = {
  '': ['Farm'],
  Farm: ['Field', 'Zone'],
  Field: ['Zone'],
  Zone: [],
};

// An operation type's own vocabulary — see Models/OperationType.cs. Replaces
// the old SpatialLayerType Category='Operation' rows; Records reference one
// of these rather than being SpatialLayer rows themselves.
export interface OperationType {
  id: string;
  name: string;
  alias: string | null;
  sortIndex: number;
  defaultGeometryKind: GeometryKind;
  allowedGeometryKinds: GeometryKind[];
  defaultColour: string | null;
  methodIds: string[];
}

export interface OperationMethod {
  id: string;
  name: string;
  alias: string | null;
}

interface UnitOption {
  id: string;
  name: string;
}

interface RecordProductLine {
  id: string;
  // Exactly one is set — productGroupId when an import matched this line to
  // a generic ProductGroup rather than a real Product.
  productId: string | null;
  productGroupId: string | null;
  productName: string;
  rate: number;
  rateUnitId: string;
  rateUnitName: string;
}

// A record of something that happened — see Models/Record.cs. Deliberately
// not a SpatialLayer/SpatialFeature: spatialFeatureId/geometry/locationName
// are each optional and independent, matching the backend's three ways of
// anchoring a record.
export interface RecordItem {
  id: string;
  businessId: string;
  // Only meaningful when a records list spans multiple businesses (the
  // admin Records page's "include child businesses" option) — undefined
  // otherwise.
  businessName?: string | null;
  spatialFeatureId: string | null;
  spatialFeatureName: string | null;
  farmName: string | null;
  fieldName: string | null;
  farmId?: string | null;
  fieldId?: string | null;
  geometry: GeoShape | null;
  locationName: string | null;
  // regionName = raw/denormalized text; linkedRegionName = the associated
  // Region's own name (Record.RegionId) — what regional reporting groups by.
  regionName: string | null;
  regionId: string | null;
  linkedRegionName: string | null;
  operationTypeId: string;
  operationTypeName: string;
  operationMethodId: string | null;
  operationMethodName: string | null;
  timePeriodId: string | null;
  timePeriodName: string | null;
  geneStackName: string | null;
  // Irrigated / Dryland on the record's date, from its field season — only
  // sent by the Records table's list (GET .../records).
  irrigationTypeName?: string | null;
  timeFrom: string;
  timeTo: string | null;
  notes: string | null;
  waterRate: number | null;
  waterRateUnitId: string | null;
  waterRateUnitName: string | null;
  // Stated/override area vs mapped (polygon) area — see Models/Record.cs.
  areaHectares: number | null;
  mappedAreaHectares: number | null;
  // areaHectares ?? mappedAreaHectares, computed server-side.
  effectiveAreaHectares: number | null;
  // Share of that area treated (utils/bandRate); null is all of it.
  bandPercent: number | null;
  // effectiveAreaHectares × bandPercent, computed server-side.
  affectedAreaHectares: number | null;
  products: RecordProductLine[];
}

interface IntersectResult {
  id: string;
  spatialLayerId: string;
  layerType: string;
  name: string;
}

interface MapDevice {
  id: string;
  name: string;
  deviceTypeName?: string;
  latitude: number;
  longitude: number;
  childCount: number;
}

// Business-independent reference boundary (State/SA4/LGA/named region) —
// unlike SpatialLayer, not scoped to the current business, fetched from the
// global /api/regions endpoint. Geometry is real GeoJSON (Polygon or
// MultiPolygon), not the flat GeoShape point-list SpatialFeature uses, since
// regions are only ever displayed, never drawn/edited here.
interface Region {
  id: string;
  name: string;
  regionType: string;
  parentRegionId: string | null;
  geometry: GeoJSON.Geometry;
  colour: string | null;
  fillOpacity: number | null;
}

const REGION_TYPE_COLOURS: Record<string, string> = {
  SA4: '#9575cd',
  LGA: '#4db6ac',
};
const regionColour = (regionType: string) => REGION_TYPE_COLOURS[regionType] ?? '#90a4ae';

// Regions store real GeoJSON (Polygon/MultiPolygon — see the Region comment
// above), not the flat GeoShape point-list ZoneDrawControl hands back, so a
// freshly-drawn/redrawn boundary needs converting before it's sent to
// /api/regions. Always a single-ring Polygon since it's user-drawn, unlike
// the MultiPolygon shapes imported LGA/SA4 data can carry.
const pointsToGeoJsonPolygon = (points: LatLngPoint[]): GeoJSON.Geometry => {
  const ring = points.map((p) => [p.lng, p.lat]);
  if (ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
    ring.push(ring[0]);
  }
  return { type: 'Polygon', coordinates: [ring] };
};

const LAYER_COLORS = ['#4fc3f7', '#81c784', '#ffb74d', '#e57373', '#ba68c8', '#4db6ac'];
const AUSTRALIA_CENTER: [number, number] = [-23.6980, 133.8807];

const layerPointIcon = (color: string) => L.divIcon({
  className: 'layer-point-marker',
  html: `<span style="display:block;width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 3px rgba(0,0,0,0.6)"></span>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const MapCenterController: React.FC<{ center: [number, number]; zoom: number }> = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
};

// Reports the cursor's map coordinates (for the status-bar lat/lng readout)
// as the mouse moves over the map, clearing them when it leaves.
const MapMouseTracker: React.FC<{ onMove: (latLng: { lat: number; lng: number } | null) => void }> = ({ onMove }) => {
  useMapEvents({
    mousemove: (e) => onMove({ lat: e.latlng.lat, lng: e.latlng.lng }),
    mouseout: () => onMove(null),
  });
  return null;
};

const noopMouseMove = () => {};

const mapViewStorageKey = (businessId: string) => `dmotrak.mapView.${businessId}`;

interface SavedMapView {
  lat: number;
  lng: number;
  zoom: number;
}

const loadSavedMapView = (businessId: string): SavedMapView | null => {
  try {
    const raw = localStorage.getItem(mapViewStorageKey(businessId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.lat !== 'number' || typeof parsed.lng !== 'number' || typeof parsed.zoom !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
};

// Persists the map's live pan/zoom per business, so returning to this
// business's Map tab later (or after this page just re-fetches data and
// briefly changes mapCenter/mapZoom's deps) restores where the user was
// instead of re-centering on the computed average of every feature/device.
const MapViewPersister: React.FC<{ businessId: string }> = ({ businessId }) => {
  const map = useMapEvents({
    moveend: () => {
      const center = map.getCenter();
      try {
        localStorage.setItem(
          mapViewStorageKey(businessId),
          JSON.stringify({ lat: center.lat, lng: center.lng, zoom: map.getZoom() })
        );
      } catch {
        /* ignore (private browsing, storage full, etc.) */
      }
    },
  });
  return null;
};

// One-shot pan/zoom request, set imperatively (e.g. from a sidebar row click)
// rather than derived reactively from render state — so it only moves the map
// when the user actually asks to focus something, not on every re-render.
type FocusRequest =
  | { bounds: [[number, number], [number, number]] }
  | { center: [number, number]; zoom: number };

const MapFocusController: React.FC<{ request: FocusRequest | null }> = ({ request }) => {
  const map = useMap();
  useEffect(() => {
    if (!request) return;
    if ('bounds' in request) {
      // padding leaves a margin around the shape rather than a pixel-tight
      // fit, so the zoom lands slightly looser than the shape's true extent;
      // maxZoom keeps a single small point/paddock from zooming in absurdly far.
      map.fitBounds(request.bounds, { padding: [64, 64], maxZoom: 18 });
    } else {
      map.setView(request.center, request.zoom);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, map]);
  return null;
};

// Bounding box (with a little padding for Points, which have no extent of
// their own) to focus the map on when a feature is selected from the sidebar.
const focusRequestForFeature = (feature: { geometry: GeoShape | null }): FocusRequest | null => {
  const geometry = feature.geometry;
  if (!geometry || geometry.points.length === 0) return null;
  if (geometry.geometryType === 'Point') {
    const p = geometry.points[0];
    return { center: [p.lat, p.lng], zoom: 16 };
  }
  const lats = geometry.points.map((p) => p.lat);
  const lngs = geometry.points.map((p) => p.lng);
  return {
    bounds: [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)],
    ],
  };
};

interface Props {
  businessId: string;
  businessName: string;
}

const toIso = (localDateTime: string): string | undefined => (localDateTime ? new Date(localDateTime).toISOString() : undefined);

const pad = (n: number) => String(n).padStart(2, '0');

const toDatetimeLocal = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

const defaultRangeTo = (): string => toDatetimeLocal(new Date());

const BusinessMapTab: React.FC<Props> = ({ businessId, businessName }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const isAdmin = auth.hasRole('DMO_ADMIN');
  const authHeader = { Authorization: `Bearer ${auth.tokens?.accessToken}` };
  const stableAuthHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);
  // NDVI images added from the Satellite panel — kept here, not in the
  // panel, so they stay on the map when another feature is selected.
  const satelliteImagery = useSatelliteOverlays(authHeader);

  const [sidebarTab, setSidebarTab] = useState<'layers' | 'devices'>('layers');
  // Global Layers starts collapsed — with every QLD LGA loaded that list runs
  // to 70+ rows and would otherwise dominate the sidebar on first open.
  const [businessLayersExpanded, setBusinessLayersExpanded] = useState(true);
  const [globalLayersExpanded, setGlobalLayersExpanded] = useState(false);
  // Which RegionType sub-accordions are open within Global Layers — starts
  // empty (all collapsed) since a single type (LGA) can be 70+ rows on its own.
  const [expandedRegionTypes, setExpandedRegionTypes] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'map' | 'operations' | 'other'>('operations');
  // An edit opened from the Records table detours to the Map (to show the
  // record's feature, and for drawing its shape) — closing the dialog goes
  // back to Records. recordsRefreshKey makes the table refetch after a save.
  const [returnToRecordsTab, setReturnToRecordsTab] = useState(false);
  const [recordsRefreshKey, setRecordsRefreshKey] = useState(0);
  // Which feature's operations the Operations tab is showing — set by
  // double-clicking that feature on the map or in the sidebar list.
  const [operationsRoot, setOperationsRoot] = useState<{ id: string; name: string; key: number } | null>(null);

  // Right-click context menu — which feature was clicked and where to anchor
  // the menu (screen coordinates, not map coordinates).
  const [contextMenu, setContextMenu] = useState<{ feature: SpatialFeature; mouseX: number; mouseY: number } | null>(null);

  // "Add/Edit Operation" (Record) dialog — opened from the context menu (add)
  // or by clicking a row in the Records tab (edit). recordParent is the
  // feature the record is anchored to; it stays set across a draw-a-shape
  // detour (dialog closes, map opens for drawing, dialog reopens with the
  // drawn geometry attached) so recordDialogOpen is tracked separately from
  // "is a record being composed at all". recordEditingId is null when
  // creating, or the id of the Record being edited.
  const [recordParent, setRecordParent] = useState<SpatialFeature | null>(null);
  const [recordEditingId, setRecordEditingId] = useState<string | null>(null);
  // The record as it was when its edit dialog opened, and which tab shows:
  // the form, or what it was imported as (only when it was imported).
  const [recordEditingItem, setRecordEditingItem] = useState<RecordItem | null>(null);
  const [recordTab, setRecordTab] = useState<'record' | 'imported'>('record');
  const { rows: recordImportRows, loading: recordImportLoading } = useRecordImportRows(recordEditingId, stableAuthHeaders);
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [recordTypeId, setRecordTypeId] = useState('');
  const [recordMethodId, setRecordMethodId] = useState('');
  const [recordTimeFrom, setRecordTimeFrom] = useState('');
  const [recordNotes, setRecordNotes] = useState('');
  const [recordWaterRate, setRecordWaterRate] = useState('');
  const [recordWaterRateUnitId, setRecordWaterRateUnitId] = useState('');
  const [recordGeometryKind, setRecordGeometryKind] = useState<GeometryKind | ''>('');
  const [recordGeometry, setRecordGeometry] = useState<GeoShape | null>(null);
  // Area (ha) override — blank means "use the mapped area". The mapped area
  // itself is derived server-side (Record.MappedAreaHectares); these two
  // remember what it was when the dialog opened so the preview below can
  // tell an untouched shape (keep the server's value — which may be an
  // import's Region fallback, deliberately not counted) from a newly
  // drawn/removed one (recompute, same rule as RecordsController).
  const [recordAreaHectares, setRecordAreaHectares] = useState('');
  const [recordBandPercent, setRecordBandPercent] = useState('');
  const [recordInitialGeometry, setRecordInitialGeometry] = useState<GeoShape | null>(null);
  const [recordInitialMappedArea, setRecordInitialMappedArea] = useState<number | null>(null);
  // Region line of the record being edited (see recordRegionLabel).
  const [recordEditingRegionLabel, setRecordEditingRegionLabel] = useState<string | null>(null);
  // The edited record's linked Region (Record.RegionId) — only sent on save
  // when changed, so the record's own source text (RegionName) stays.
  const [recordRegionId, setRecordRegionId] = useState<string | null>(null);
  const [recordProducts, setRecordProducts] = useState<
    { productId: string | null; productGroupId: string | null; productName: string; rate: string; rateUnitId: string }[]
  >([]);
  const [recordSaving, setRecordSaving] = useState(false);
  const [recordError, setRecordError] = useState<string | null>(null);

  const [operationTypes, setOperationTypes] = useState<OperationType[]>([]);
  const [operationMethods, setOperationMethods] = useState<OperationMethod[]>([]);
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [records, setRecords] = useState<RecordItem[]>([]);

  const [layers, setLayers] = useState<SpatialLayer[]>([]);
  const [features, setFeatures] = useState<SpatialFeature[]>([]);
  const [loadingLayers, setLoadingLayers] = useState(true);
  const [layerTypes, setLayerTypes] = useState<SpatialLayerType[]>([]);
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);
  // Features individually toggled off via the sidebar checkbox. Unchecking a
  // parent hides it and cascades to hide all its descendants (set here, not
  // computed at render time), but a descendant can still be individually
  // re-checked afterwards to show just that one feature again.
  const [hiddenFeatureIds, setHiddenFeatureIds] = useState<Set<string>>(new Set());

  // Vertex editing — dragging an existing shape's points rather than redrawing
  // it from scratch. editSnapshot freezes the geometry at the moment editing
  // starts so unrelated re-renders (which recompute positions fresh from
  // `features`) don't fight the in-progress drag; Leaflet's own editing handles
  // own the live coordinates until Save/Cancel. featureRefs holds the actual
  // Leaflet Polygon/Polyline instances (leaflet-draw adds `.editing` to their
  // prototype), keyed by feature id, so Save/Cancel can read back or reset them.
  const [editingFeatureId, setEditingFeatureId] = useState<string | null>(null);
  const [editSnapshot, setEditSnapshot] = useState<GeoShape | null>(null);
  const featureRefs = useRef<Map<string, L.Polygon | L.Polyline>>(new Map());

  // Sidebar width, resizable by dragging the handle between it and the map
  // (desktop/tablet only — below `md` the sidebar becomes a toggleable
  // overlay instead of a permanent side-by-side split, since a fixed-width
  // sidebar has no room to share with the map on a phone).
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const [cursorLatLng, setCursorLatLng] = useState<{ lat: number; lng: number } | null>(null);
  // SpatialImage.url points at an authenticated GET action, so it can't be
  // used directly as an ImageOverlay src — fetched as a blob (with the auth
  // header) and rendered via an object URL, same pattern already used for
  // device snapshot thumbnails elsewhere in this app.
  const [imageObjectUrls, setImageObjectUrls] = useState<Map<string, string>>(new Map());
  // Street (OpenStreetMap) vs Satellite (Esri World Imagery — free, no API
  // key, no self-hosting) basemap. A quick win in place of self-hosting
  // Sentinel-2 imagery, which is real infrastructure work parked for later
  // (see Architecture.md's backlog). Persisted globally, not per-business —
  // it's a viewing preference, not business data.
  const [baseLayer, setBaseLayer] = useState<'street' | 'satellite'>(
    () => (localStorage.getItem('dmotrak.mapBaseLayer') === 'satellite' ? 'satellite' : 'street')
  );
  const toggleBaseLayer = () => {
    setBaseLayer((prev) => {
      const next = prev === 'street' ? 'satellite' : 'street';
      try {
        localStorage.setItem('dmotrak.mapBaseLayer', next);
      } catch {
        /* ignore */
      }
      return next;
    });
  };
  const resizeDragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    resizeDragRef.current = { startX: e.clientX, startWidth: sidebarWidth };
    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizeDragRef.current) return;
      const delta = moveEvent.clientX - resizeDragRef.current.startX;
      setSidebarWidth(Math.min(600, Math.max(220, resizeDragRef.current.startWidth + delta)));
    };
    const handleMouseUp = () => {
      resizeDragRef.current = null;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Which parent features have their children collapsed in the sidebar tree.
  const [collapsedFeatureIds, setCollapsedFeatureIds] = useState<Set<string>>(new Set());
  const toggleCollapsed = (featureId: string) => {
    setCollapsedFeatureIds((prev) => {
      const next = new Set(prev);
      if (next.has(featureId)) next.delete(featureId);
      else next.add(featureId);
      return next;
    });
  };

  // Reference regions — fetched once (global, not per-business), hidden by
  // default since a single SA4 can be 100,000+ km² and would swamp the view
  // for anyone working a single farm; shown only once explicitly checked on.
  const [regions, setRegions] = useState<Region[]>([]);
  const [visibleRegionIds, setVisibleRegionIds] = useState<Set<string>>(new Set());
  // Live pass-through overlay, not imported into our own Region table like
  // the SA4/LGA boundaries — Queensland's cadastral parcel dataset (DCDB) is
  // far too large and changes too often (updated nightly) to mirror. Source:
  // Qld Dept of Resources' public WMS (spatial-gis.information.qld.gov.au).
  const [showQldCadastre, setShowQldCadastre] = useState(false);
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);

  // Region create/edit — 'create' = post-draw naming step (needs
  // pendingRegionGeometry), 'edit' = renaming/re-parenting an existing region
  // with no geometry change (a separate "Redraw" action handles that, via
  // drawMode 'region-redraw' below, same split as feature form/redraw).
  const [regionFormOpen, setRegionFormOpen] = useState(false);
  const [regionFormMode, setRegionFormMode] = useState<'create' | 'edit'>('create');
  const [regionFormName, setRegionFormName] = useState('');
  const [regionFormType, setRegionFormType] = useState('');
  const [regionFormParentId, setRegionFormParentId] = useState('');
  const [regionFormColour, setRegionFormColour] = useState('#90a4ae');
  const [regionFormOpacity, setRegionFormOpacity] = useState(0.15);
  const [regionEditTarget, setRegionEditTarget] = useState<Region | null>(null);
  const [pendingRegionGeometry, setPendingRegionGeometry] = useState<GeoJSON.Geometry | null>(null);
  const [savingRegion, setSavingRegion] = useState(false);
  const [deleteRegionTarget, setDeleteRegionTarget] = useState<Region | null>(null);
  const [deletingRegion, setDeletingRegion] = useState(false);

  const [devices, setDevices] = useState<MapDevice[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [focusedDeviceId, setFocusedDeviceId] = useState<string | null>(null);
  const [setLocationOpen, setSetLocationOpen] = useState(false);

  // Draw workflow: pick a type first (which fixes the geometry kind), then draw,
  // then (for create) name it. 'record' is a detour from the Add Operation
  // dialog to draw that record's own optional shape. 'region-create'/
  // 'region-redraw' are the same idea for a Global Layer (Region) boundary.
  const [drawMode, setDrawMode] = useState<'none' | 'picking' | 'create' | 'redraw' | 'record' | 'region-create' | 'region-redraw'>('none');
  // The feature a newly-created feature will be parented under (via its
  // layer's ParentSpatialFeatureId) — null for a top-level Farm.
  const [pendingParentFeatureId, setPendingParentFeatureId] = useState<string | null>(null);
  const [pendingTypeId, setPendingTypeId] = useState('');
  // Set when the picking step was entered via a category-bar button, to filter
  // the type dropdown down to that category. Null when entered via the generic
  // sidebar Add button, which offers every type.
  const [pendingCategory, setPendingCategory] = useState<LayerCategory | null>(null);
  // Which of the type's allowed geometry kinds to draw — most types only
  // allow one, but some (e.g. a management zone drawn as an as-applied swath)
  // allow more than one, so this is user-selectable when there's more than
  // one option.
  const [pendingGeometryKind, setPendingGeometryKind] = useState<GeometryKind | ''>('');
  const [pendingGeometry, setPendingGeometry] = useState<GeoShape | null>(null);

  // 'create' = post-draw naming step (needs pendingGeometry); 'edit' = editing
  // an existing feature's Name/Colour/Device/Notes with no geometry involved.
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [formName, setFormName] = useState('');
  const [formDeviceId, setFormDeviceId] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formColour, setFormColour] = useState('#4fc3f7');
  const [formOpacity, setFormOpacity] = useState(0.4);
  // Only offered/sent when editing a Farm with no boundary of its own — see
  // startRegionCreate/the Region model comment for why this exists.
  const [formFallbackRegionId, setFormFallbackRegionId] = useState('');
  // Manually editable only while the feature has no Polygon boundary — once
  // one's drawn, the server always recomputes this from geometry instead
  // (see SpatialFeaturesController.ComputeAreaHectares), so this stays a
  // read-only display of that computed value from then on.
  const [formAreaHectares, setFormAreaHectares] = useState('');
  // Free-form recorded data for the feature (e.g. a zone's soil type, a
  // probe's serial number) — edited as a simple key/value row list, saved as
  // Record<string,string> (AttributesJson on the backend).
  const [formAttributes, setFormAttributes] = useState<{ key: string; value: string }[]>([]);
  // Only used when creating an "Image Overlay" feature — uploaded right
  // after the feature itself is created (the feature needs an id first).
  const [formImageFile, setFormImageFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SpatialFeature | null>(null);

  const [intersects, setIntersects] = useState<IntersectResult[]>([]);
  const [loadingIntersects, setLoadingIntersects] = useState(false);

  const [typesDialogOpen, setTypesDialogOpen] = useState(false);

  const selectedFeature = features.find((f) => f.id === selectedFeatureId) ?? null;
  const selectedRegion = regions.find((r) => r.id === selectedRegionId) ?? null;
  const regionsByType = useMemo(() => {
    const map = new Map<string, Region[]>();
    for (const r of regions) {
      const key = r.regionType || 'Other';
      const list = map.get(key);
      if (list) list.push(r); else map.set(key, [r]);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [regions]);
  const toggleRegionTypeExpanded = (regionType: string) => {
    setExpandedRegionTypes((prev) => {
      const next = new Set(prev);
      if (next.has(regionType)) next.delete(regionType); else next.add(regionType);
      return next;
    });
  };
  const layerOf = (feature: SpatialFeature | null | undefined): SpatialLayer | null =>
    feature ? layers.find((l) => l.id === feature.spatialLayerId) ?? null : null;
  const pendingType = layerTypes.find((t) => t.id === pendingTypeId) ?? null;
  const selectedFeatureLayer = layerOf(selectedFeature);
  const allowedChildCategories = CHILD_CATEGORIES_FOR_PARENT[selectedFeatureLayer?.category ?? ''] ?? [];

  const fetchLayers = async () => {
    setLoadingLayers(true);
    try {
      const res = await fetch(`/api/businesses/${businessId}/spatial-layers`, { headers: authHeader });
      if (res.ok) {
        setLayers(await res.json());
      }
    } catch {
      /* ignore */
    } finally {
      setLoadingLayers(false);
    }
  };

  const fetchFeatures = async () => {
    try {
      const res = await fetch(`/api/businesses/${businessId}/spatial-features`, { headers: authHeader });
      if (res.ok) {
        setFeatures(await res.json());
      }
    } catch {
      /* ignore */
    }
  };

  const fetchDevices = async () => {
    setLoadingDevices(true);
    try {
      const res = await fetch(`/api/devices/map?businessId=${businessId}`, { headers: authHeader });
      if (res.ok) {
        setDevices(await res.json());
      }
    } catch {
      /* ignore */
    } finally {
      setLoadingDevices(false);
    }
  };

  const fetchLayerTypes = async () => {
    try {
      const res = await fetch('/api/spatial-layer-types', { headers: authHeader });
      if (res.ok) {
        setLayerTypes(await res.json());
      }
    } catch {
      /* ignore */
    }
  };

  const fetchRegions = async () => {
    try {
      const res = await fetch('/api/regions', { headers: authHeader });
      if (res.ok) {
        setRegions(await res.json());
      }
    } catch {
      /* ignore */
    }
  };

  const fetchOperationTypes = async () => {
    try {
      const res = await fetch('/api/operation-types', { headers: authHeader });
      if (res.ok) setOperationTypes(await res.json());
    } catch {
      /* ignore */
    }
  };

  const fetchOperationMethods = async () => {
    try {
      const res = await fetch('/api/operation-methods', { headers: authHeader });
      if (res.ok) setOperationMethods(await res.json());
    } catch {
      /* ignore */
    }
  };

  const fetchUnits = async () => {
    try {
      const res = await fetch('/api/units', { headers: authHeader });
      if (res.ok) setUnits(await res.json());
    } catch {
      /* ignore */
    }
  };

  const fetchRecords = async () => {
    try {
      const res = await fetch(`/api/businesses/${businessId}/records`, { headers: authHeader });
      if (res.ok) setRecords(await res.json());
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    fetchLayers();
    fetchFeatures();
    fetchDevices();
    fetchLayerTypes();
    fetchRegions();
    fetchOperationTypes();
    fetchOperationMethods();
    fetchUnits();
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  // Farms start collapsed by default (there's usually more going on under a
  // Farm than anyone wants to see at once) — but only applied once on initial
  // load, so it doesn't keep re-collapsing a farm the user deliberately
  // expanded after every later refetch.
  const initialCollapseDone = useRef(false);
  useEffect(() => {
    if (initialCollapseDone.current || features.length === 0 || layers.length === 0 || layerTypes.length === 0) return;
    const farmFeatureIds = features
      .filter((f) => layerOf(f)?.category === 'Farm')
      .map((f) => f.id);
    if (farmFeatureIds.length > 0) {
      setCollapsedFeatureIds(new Set(farmFeatureIds));
    }
    initialCollapseDone.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features, layers, layerTypes]);

  // Fetch (as authenticated blobs) any image-bearing feature that isn't
  // already cached, and drop cached entries for features that no longer have
  // one (deleted/replaced) — revoking their object URLs so they don't leak.
  useEffect(() => {
    const currentImageFeatures = features.filter((f) => f.image);
    const currentIds = new Set(currentImageFeatures.map((f) => f.id));

    setImageObjectUrls((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const [id, url] of prev) {
        if (!currentIds.has(id)) {
          URL.revokeObjectURL(url);
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    currentImageFeatures
      .filter((f) => !imageObjectUrls.has(f.id))
      .forEach((f) => {
        fetch(f.image!.url, { headers: authHeader })
          .then((res) => (res.ok ? res.blob() : null))
          .then((blob) => {
            if (!blob) return;
            const objectUrl = URL.createObjectURL(blob);
            setImageObjectUrls((prev) => new Map(prev).set(f.id, objectUrl));
          })
          .catch(() => {
            /* ignore */
          });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features]);

  useEffect(() => {
    if (!selectedFeatureId) {
      setIntersects([]);
      return;
    }
    setLoadingIntersects(true);
    fetch(`/api/spatial-features/${selectedFeatureId}/intersects`, { headers: authHeader })
      .then((res) => (res.ok ? res.json() : []))
      .then((data: IntersectResult[]) => setIntersects(data))
      .catch(() => setIntersects([]))
      .finally(() => setLoadingIntersects(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeatureId]);

  // Loaded once per business, not kept live in sync with the map — the map's
  // actual live position is Leaflet's own state, continuously written back
  // out by MapViewPersister. This is only ever read to pick the *initial*
  // center/zoom.
  const savedMapView = useMemo(() => loadSavedMapView(businessId), [businessId]);

  // Memoized on the actual position data (not recomputed into a fresh array on
  // every render) so MapCenterController's effect — which keys off this array's
  // *reference* — only fires when the map genuinely needs to recenter (new
  // feature/device data, or focusing a device). Once a saved view exists for
  // this business, it wins outright and stays stable across feature/device
  // refetches (a fetch after any edit would otherwise create a new array
  // reference and snap the map back to the computed average, undoing
  // wherever the user had actually navigated to).
  const mapCenter = useMemo<[number, number]>(() => {
    if (focusedDeviceId) {
      const focused = devices.find((d) => d.id === focusedDeviceId);
      if (focused) return [focused.latitude, focused.longitude];
    }
    if (savedMapView) return [savedMapView.lat, savedMapView.lng];
    const featurePoints = features.flatMap((f) => f.geometry?.points ?? []);
    const devicePoints = devices.map((d) => ({ lat: d.latitude, lng: d.longitude }));
    const points = [...featurePoints, ...devicePoints];
    if (points.length === 0) return AUSTRALIA_CENTER;
    const avgLat = points.reduce((s, p) => s + p.lat, 0) / points.length;
    const avgLng = points.reduce((s, p) => s + p.lng, 0) / points.length;
    return [avgLat, avgLng];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedDeviceId, devices, features, savedMapView]);

  const mapZoom = useMemo(() => {
    if (focusedDeviceId) return 13;
    return savedMapView?.zoom ?? 6;
  }, [focusedDeviceId, savedMapView]);

  // Precomputed once per actual data change (not on every re-render) so the
  // Polygon/Polyline `positions` prop keeps the same array *reference* when
  // nothing relevant changed. Without this, a plain `.map()` inline in the
  // render loop returns a fresh array every render — and since
  // MapMouseTracker's mousemove handler re-renders this component
  // continuously while the cursor moves (i.e. throughout a vertex drag),
  // react-leaflet would see a "changed" prop on every tick and call
  // setLatLngs() with the frozen editSnapshot values, snapping the shape
  // back and undoing the drag before the user can see it move. Same class of
  // bug as the mapCenter-recompute issue above, just for per-vertex edits.
  const positionsByFeatureId = useMemo(() => {
    const map = new Map<string, [number, number][]>();
    for (const feature of features) {
      const geometry = feature.id === editingFeatureId && editSnapshot ? editSnapshot : feature.geometry;
      if (!geometry) continue;
      map.set(feature.id, geometry.points.map((p) => [p.lat, p.lng] as [number, number]));
    }
    return map;
  }, [features, editingFeatureId, editSnapshot]);

  const startCreate = (parentFeatureId: string | null) => {
    setSelectedFeatureId(null);
    setPendingParentFeatureId(parentFeatureId);
    setPendingTypeId('');
    setPendingCategory(null);
    setPendingGeometryKind('');
    setPendingGeometry(null);
    setDrawMode('picking');
  };

  // Entry point for the category bar. If exactly one type exists for the
  // category (Farm/Field/Zone today), skip the picking step and go straight to
  // drawing.
  const startCreateForCategory = (parentFeatureId: string | null, category: LayerCategory) => {
    const typesInCategory = layerTypes.filter((t) => t.category === category);
    setSelectedFeatureId(null);
    setPendingParentFeatureId(parentFeatureId);
    setPendingGeometry(null);
    if (typesInCategory.length === 1) {
      setPendingTypeId(typesInCategory[0].id);
      setPendingGeometryKind(typesInCategory[0].defaultGeometryKind);
      setPendingCategory(null);
      setDrawMode('create');
    } else {
      setPendingTypeId('');
      setPendingGeometryKind('');
      setPendingCategory(category);
      setDrawMode('picking');
    }
  };

  const beginDrawing = () => {
    if (!pendingType) return;
    setDrawMode('create');
  };

  const startRedraw = (feature: SpatialFeature) => {
    const type = layerOf(feature);
    setSelectedFeatureId(feature.id);
    setPendingTypeId(type?.layerTypeId ?? '');
    // Redraw keeps the feature's existing geometry kind rather than exposing a
    // kind-switcher — if a type allows more than one kind, changing which one
    // a feature uses is a bigger decision than fixing a mis-drawn shape.
    setPendingGeometryKind(feature.geometry?.geometryType ?? type?.defaultGeometryKind ?? '');
    setPendingGeometry(null);
    setDrawMode('redraw');
  };

  const handleDrawn = (geometryType: GeometryKind, points: LatLngPoint[]) => {
    if (drawMode === 'region-create') {
      setPendingRegionGeometry(pointsToGeoJsonPolygon(points));
      setRegionFormMode('create');
      setRegionEditTarget(null);
      setRegionFormName('');
      setRegionFormType('');
      setRegionFormParentId('');
      setRegionFormColour('#90a4ae');
      setRegionFormOpacity(0.15);
      setRegionFormOpen(true);
      return;
    }
    if (drawMode === 'region-redraw' && selectedRegionId) {
      saveRegionRedraw(selectedRegionId, pointsToGeoJsonPolygon(points));
      return;
    }
    const geometry = { geometryType, points };
    setPendingGeometry(geometry);
    if (drawMode === 'create') {
      setFormMode('create');
      setFormName('');
      setFormDeviceId('');
      setFormNotes('');
      setFormColour(pendingType?.defaultColour || '#4fc3f7');
      setFormOpacity(0.4);
      setFormFallbackRegionId('');
      setFormAttributes([]);
      setFormImageFile(null);
      setFormOpen(true);
    } else if (drawMode === 'redraw' && selectedFeature) {
      saveRedraw(selectedFeature.id, geometry);
    } else if (drawMode === 'record') {
      setRecordGeometry(geometry);
      setDrawMode('none');
      setRecordDialogOpen(true);
    }
  };

  // Disables leaflet-draw's vertex-editing handler when editing ends. Looks
  // up the instance FRESH in the cleanup (not a closed-over reference) so it
  // always disables whichever instance is actually current, even if the
  // Polygon/Polyline was remounted mid-edit (see enableEditingOnInstance).
  useEffect(() => {
    if (!editingFeatureId) return;
    const idBeingEdited = editingFeatureId;
    return () => {
      const instance = featureRefs.current.get(idBeingEdited) as any;
      console.log('[vertex-edit] disabling for feature', idBeingEdited);
      instance?.editing?.disable();
      prevEditingInstanceRef.current = null;
    };
  }, [editingFeatureId]);

  // Called from the Polygon/Polyline ref callback below on every SET, not
  // just once per editingFeatureId change. react-leaflet has been observed
  // to recreate the underlying Leaflet layer mid-edit-session (ref fires
  // CLEARED then SET again); if that happens while a feature is being
  // edited, the OLD instance keeps its enabled editing handles (orphaned —
  // draggable but no longer the instance saveVertexEdit reads back), while
  // the NEW instance never gets .editing.enable() called on it at all. This
  // is the leading theory for why drags appeared to "do something" (edit
  // event fired) but saves persisted the untouched original geometry.
  // Re-applying enable/disable on every ref-set keeps whichever instance is
  // CURRENTLY tracked in featureRefs in sync with what's actually editable.
  const prevEditingInstanceRef = useRef<any>(null);
  const enableEditingOnInstance = (featureId: string, instance: any) => {
    if (featureId !== editingFeatureId || !instance) return;
    const prev = prevEditingInstanceRef.current;
    if (prev && prev !== instance) {
      console.log('[vertex-edit] instance CHANGED mid-edit for', featureId, '— disabling stale instance, enabling fresh one');
      prev.editing?.disable();
    }
    if (prev !== instance) {
      console.log('[vertex-edit] enabling for feature', featureId, {
        hasEditing: !!instance.editing,
        alreadyEnabled: instance.editing?._enabled,
      });
      instance.editing?.enable();
      // TEMPORARY diagnostic — directly verify that the vertex markers
      // leaflet-draw just created are wired (by object reference) to the
      // SAME LatLng objects the polygon itself stores in _latlngs. If this
      // is false, dragging a marker mutates an object the polygon never
      // reads back from, explaining edit events firing with unchanged
      // coordinates.
      try {
        const ring = instance._latlngs?.[0];
        const vh = instance.editing?._verticesHandlers?.[0];
        const marker0 = vh?._markers?.[0];
        console.log('[vertex-edit] reference check —', {
          ringIsVhLatlngs: ring === vh?._latlngs?.[0],
          marker0OrigLatLngIsRingPoint0: marker0?._origLatLng === ring?.[0],
          ringLength: ring?.length,
          vhLatlngsLength: vh?._latlngs?.length,
        });
      } catch (e) {
        console.log('[vertex-edit] reference check failed', e);
      }
      // TEMPORARY diagnostic — wrap setLatLngs to catch, with a stack trace,
      // anything that resets this polygon's coordinates during the edit
      // session (react-leaflet's Polygon calls layer.setLatLngs(positions)
      // whenever the `positions` prop reference changes — this proves
      // whether that's happening here despite the memoized, stable
      // `positions` array).
      const originalSetLatLngs = instance.setLatLngs.bind(instance);
      instance.setLatLngs = (...args: any[]) => {
        console.log('[vertex-edit] setLatLngs() called on', featureId, 'with', JSON.stringify(args), new Error('stack').stack);
        return originalSetLatLngs(...args);
      };
      instance.on?.('edit', () => {
        // Extract primitive lat/lng NUMBERS immediately — logging the live
        // L.LatLng[] array directly is misleading: Chrome's console lazily
        // renders object previews, so if Leaflet later mutates those same
        // object instances (or a re-render calls setLatLngs on them) before
        // you expand the log line, the panel shows the CURRENT state, not
        // what was true at log-time. Plain numbers can't be retroactively
        // changed like that.
        const ring = (instance.getLatLngs?.()[0] ?? instance.getLatLngs?.()) as L.LatLng[];
        // Also log the DRAGGED MARKER's own current position — if this
        // differs from ring[0] above, the marker moved but the polygon's
        // stored data didn't, proving the two are disconnected objects.
        const marker0 = instance.editing?._verticesHandlers?.[0]?._markers?.[0];
        const marker0LatLng = marker0?.getLatLng?.();
        console.log('[vertex-edit] "edit" event fired on', featureId, 'ring coords:', JSON.stringify(ring?.map((p) => [p.lat, p.lng])), 'marker0 current pos:', marker0LatLng ? JSON.stringify([marker0LatLng.lat, marker0LatLng.lng]) : 'n/a');
      });
      prevEditingInstanceRef.current = instance;
    }
  };

  const startVertexEdit = (feature: SpatialFeature) => {
    if (drawMode !== 'none' || !feature.geometry) return;
    if (feature.geometry.geometryType !== 'Polygon' && feature.geometry.geometryType !== 'LineString') return;
    console.log('[vertex-edit] startVertexEdit', feature.id, 'instance in featureRefs?', featureRefs.current.has(feature.id));
    setSelectedFeatureId(feature.id);
    setEditSnapshot(feature.geometry);
    setEditingFeatureId(feature.id);
    // Zoom to (slightly looser than) the shape's extent so its points are
    // comfortably reachable for dragging, rather than staying wherever the
    // view happened to be when the context menu was opened.
    setFocusRequest(focusRequestForFeature(feature));
  };

  const cancelVertexEdit = () => {
    if (editingFeatureId) {
      const instance = featureRefs.current.get(editingFeatureId);
      const original = editSnapshot;
      if (instance && original) {
        // Explicitly reset the live Leaflet instance's coordinates rather than
        // relying on the next render to do it — editing.disable() alone
        // doesn't revert dragged points.
        const latlngs = original.points.map((p) => L.latLng(p.lat, p.lng));
        (instance as L.Polygon).setLatLngs(original.geometryType === 'Polygon' ? [latlngs] : latlngs);
      }
    }
    setEditingFeatureId(null);
    setEditSnapshot(null);
  };

  const saveVertexEdit = async () => {
    if (!editingFeatureId) return;
    const instance = featureRefs.current.get(editingFeatureId);
    if (!instance) return;
    console.log('[vertex-edit] saveVertexEdit — live coords right now:', JSON.stringify(((instance as any).getLatLngs?.()[0] ?? (instance as any).getLatLngs?.())?.map((p: L.LatLng) => [p.lat, p.lng])));
    console.log('[vertex-edit] editSnapshot coords (fallback if unchanged):', JSON.stringify(editSnapshot?.points.map((p) => [p.lat, p.lng])));
    const geometry: GeoShape = editSnapshot!.geometryType === 'Polygon'
      ? { geometryType: 'Polygon', points: ((instance as L.Polygon).getLatLngs()[0] as L.LatLng[]).map((p) => ({ lat: p.lat, lng: p.lng })) }
      : { geometryType: 'LineString', points: ((instance as L.Polyline).getLatLngs() as L.LatLng[]).map((p) => ({ lat: p.lat, lng: p.lng })) };
    console.log('[vertex-edit] geometry about to PUT (extracted primitives, immutable):', JSON.stringify(geometry));
    await saveRedraw(editingFeatureId, geometry);
    setEditingFeatureId(null);
    setEditSnapshot(null);
  };

  const saveRedraw = async (featureId: string, geometry: GeoShape) => {
    setSaving(true);
    setError(null);
    try {
      const requestBody = JSON.stringify({ geometry });
      console.log('[vertex-edit] PUT request body actually sent over the wire:', requestBody);
      const res = await fetch(`/api/spatial-features/${featureId}`, {
        method: 'PUT',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: requestBody,
      });
      if (!res.ok) throw new Error('Failed to save shape');
      const responseText = await res.text();
      console.log('[vertex-edit] PUT response body:', responseText);
      const updated = JSON.parse(responseText);
      await fetchFeatures();
      setDrawMode('none');
      setPendingGeometry(null);
      // Re-centers on the shape's new extent — relevant since it may have
      // moved or resized significantly (e.g. after vertex-editing).
      setFocusRequest(focusRequestForFeature(updated));
    } catch {
      setError('Failed to save the new shape');
    } finally {
      setSaving(false);
    }
  };

  const startRegionCreate = () => {
    setSelectedFeatureId(null);
    setSelectedRegionId(null);
    setPendingRegionGeometry(null);
    setDrawMode('region-create');
  };

  const startRegionRedraw = (region: Region) => {
    setSelectedFeatureId(null);
    setSelectedRegionId(region.id);
    setPendingRegionGeometry(null);
    setDrawMode('region-redraw');
  };

  const selectRegionFromSidebar = (region: Region) => {
    setSelectedFeatureId(null);
    setSelectedRegionId(region.id);
  };

  const saveRegionRedraw = async (regionId: string, geometry: GeoJSON.Geometry) => {
    const region = regions.find((r) => r.id === regionId);
    if (!region) return;
    setSavingRegion(true);
    setError(null);
    try {
      const res = await fetch(`/api/regions/${regionId}`, {
        method: 'PUT',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: region.name, regionType: region.regionType, parentRegionId: region.parentRegionId, geometry }),
      });
      if (!res.ok) throw new Error('Failed to save region shape');
      await fetchRegions();
      setDrawMode('none');
    } catch {
      setError('Failed to save the new region shape');
    } finally {
      setSavingRegion(false);
    }
  };

  const openRegionProperties = (region: Region) => {
    setRegionFormMode('edit');
    setRegionEditTarget(region);
    setRegionFormName(region.name);
    setRegionFormType(region.regionType);
    setRegionFormParentId(region.parentRegionId ?? '');
    setRegionFormColour(region.colour || regionColour(region.regionType));
    setRegionFormOpacity(region.fillOpacity ?? 0.15);
    setRegionFormOpen(true);
  };

  const closeRegionForm = () => {
    setRegionFormOpen(false);
    setPendingRegionGeometry(null);
    setRegionEditTarget(null);
    if (drawMode === 'region-create') setDrawMode('none');
  };

  const handleRegionFormSave = async () => {
    if (!regionFormName.trim() || !regionFormType.trim()) return;
    setSavingRegion(true);
    setError(null);
    try {
      if (regionFormMode === 'create') {
        if (!pendingRegionGeometry) return;
        const res = await fetch('/api/regions', {
          method: 'POST',
          headers: { ...authHeader, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: regionFormName.trim(),
            regionType: regionFormType.trim(),
            parentRegionId: regionFormParentId || null,
            colour: regionFormColour,
            fillOpacity: regionFormOpacity,
            geometry: pendingRegionGeometry,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to create region');
        }
        const created = await res.json();
        setSelectedRegionId(created.id);
        setDrawMode('none');
      } else {
        if (!regionEditTarget) return;
        const res = await fetch(`/api/regions/${regionEditTarget.id}`, {
          method: 'PUT',
          headers: { ...authHeader, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: regionFormName.trim(),
            regionType: regionFormType.trim(),
            parentRegionId: regionFormParentId || null,
            colour: regionFormColour,
            fillOpacity: regionFormOpacity,
            geometry: regionEditTarget.geometry,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Failed to update region');
        }
      }
      setRegionFormOpen(false);
      setPendingRegionGeometry(null);
      setRegionEditTarget(null);
      await fetchRegions();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save region');
    } finally {
      setSavingRegion(false);
    }
  };

  const handleDeleteRegionConfirmed = async () => {
    if (!deleteRegionTarget) return;
    setDeletingRegion(true);
    try {
      const res = await fetch(`/api/regions/${deleteRegionTarget.id}`, { method: 'DELETE', headers: authHeader });
      if (!res.ok) throw new Error('Failed to delete region');
      if (selectedRegionId === deleteRegionTarget.id) setSelectedRegionId(null);
      setDeleteRegionTarget(null);
      await fetchRegions();
    } catch {
      setError('Failed to delete region');
    } finally {
      setDeletingRegion(false);
    }
  };

  // Ensures a layer of the given type exists under the given parent feature,
  // creating one (named after the type) the first time it's needed — the
  // common case stays a single step (pick type, draw, name) even though a
  // feature can only be created inside a layer. An explicit "Manage Layers"
  // flow (not built yet) would be needed to create a second, separately-named
  // layer of the same type/parent, e.g. for a named shapefile import batch.
  const ensureLayerForType = async (typeId: string, parentFeatureId: string | null): Promise<SpatialLayer> => {
    const existing = layers.find((l) => l.layerTypeId === typeId && l.parentSpatialFeatureId === parentFeatureId);
    if (existing) return existing;

    const type = layerTypes.find((t) => t.id === typeId);
    const res = await fetch(`/api/businesses/${businessId}/spatial-layers`, {
      method: 'POST',
      headers: { ...authHeader, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: type?.name ?? 'Layer', layerTypeId: typeId, parentSpatialFeatureId: parentFeatureId }),
    });
    if (!res.ok) throw new Error('Failed to create layer');
    const created: SpatialLayer = await res.json();
    setLayers((prev) => [...prev, created]);
    return created;
  };

  const openEditProperties = (feature: SpatialFeature) => {
    const layer = layerOf(feature);
    const layerType = layerTypes.find((t) => t.id === layer?.layerTypeId);
    setFormMode('edit');
    setFormName(feature.name);
    setFormDeviceId(feature.deviceId || '');
    setFormNotes(feature.notes || '');
    setFormColour(feature.colour || layer?.colour || layerType?.defaultColour || '#4fc3f7');
    setFormOpacity(feature.fillOpacity ?? 0.4);
    setFormFallbackRegionId(feature.fallbackRegionId || '');
    setFormAreaHectares(feature.areaHectares != null ? String(feature.areaHectares) : '');
    setFormAttributes(Object.entries(feature.attributes || {}).map(([key, value]) => ({ key, value })));
    setFormOpen(true);
  };

  // Blank/duplicate keys are dropped rather than saved — the row list is a
  // free-typing UI, not a validated form.
  const attributesFromForm = (): Record<string, string> => {
    const result: Record<string, string> = {};
    formAttributes.forEach(({ key, value }) => {
      const trimmed = key.trim();
      if (trimmed) result[trimmed] = value;
    });
    return result;
  };

  const isImageOverlayType = pendingType?.name === 'Image Overlay';
  // What the satellite image under the cursor says, for the status bar.
  const satelliteReadout = cursorLatLng ? readOverlayAt(satelliteImagery.overlays, cursorLatLng) : null;
  // Feature fills sit above the imagery pane and would tint the colour
  // scale, so a feature overlapping a visible image is drawn as an outline.
  const visibleImageBounds = satelliteImagery.overlays.filter((o) => o.visible).map((o) => o.meta);
  const overSatelliteImage = (points: LatLngPoint[]) => {
    if (visibleImageBounds.length === 0 || points.length === 0) return false;
    let south = Infinity, north = -Infinity, west = Infinity, east = -Infinity;
    for (const p of points) {
      south = Math.min(south, p.lat);
      north = Math.max(north, p.lat);
      west = Math.min(west, p.lng);
      east = Math.max(east, p.lng);
    }
    return visibleImageBounds.some((b) => south <= b.north && north >= b.south && west <= b.east && east >= b.west);
  };

  const handleFormSave = async () => {
    if (!formName.trim()) return;
    if (formMode === 'create' && (!pendingGeometry || !pendingTypeId)) return;
    if (formMode === 'create' && isImageOverlayType && !formImageFile) return;
    if (formMode === 'edit' && !selectedFeature) return;
    setSaving(true);
    setError(null);
    try {
      if (formMode === 'create') {
        const layer = await ensureLayerForType(pendingTypeId, pendingParentFeatureId);
        const res = await fetch(`/api/spatial-layers/${layer.id}/features`, {
          method: 'POST',
          headers: { ...authHeader, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formName.trim(),
            deviceId: formDeviceId || null,
            notes: formNotes.trim() || null,
            colour: formColour,
            fillOpacity: formOpacity,
            attributes: attributesFromForm(),
            geometry: pendingGeometry,
          }),
        });
        if (!res.ok) throw new Error('Failed to create feature');
        const created = await res.json();

        if (formImageFile) {
          const imageForm = new FormData();
          imageForm.append('file', formImageFile);
          const imageRes = await fetch(`/api/spatial-features/${created.id}/image`, {
            method: 'POST',
            headers: authHeader,
            body: imageForm,
          });
          if (!imageRes.ok) throw new Error('Feature created, but the image failed to upload');
        }

        await fetchFeatures();
        setFormOpen(false);
        setDrawMode('none');
        setPendingGeometry(null);
        setFormImageFile(null);
        setSelectedFeatureId(created.id);
        // Without this, the map keeps showing whatever was in view before —
        // the new shape exists and is selected in the list, but if it's
        // elsewhere on the map the view never moves to actually show it.
        setFocusRequest(focusRequestForFeature(created));
      } else if (selectedFeature) {
        const res = await fetch(`/api/spatial-features/${selectedFeature.id}`, {
          method: 'PUT',
          headers: { ...authHeader, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formName.trim(),
            // The API treats a null deviceId as "leave unchanged" (same as Name/
            // Notes) and uses the empty GUID as the explicit "clear" sentinel.
            deviceId: formDeviceId || '00000000-0000-0000-0000-000000000000',
            notes: formNotes.trim() || null,
            colour: formColour,
            fillOpacity: formOpacity,
            attributes: attributesFromForm(),
            fallbackRegionId: formFallbackRegionId || null,
            clearFallbackRegionId: !formFallbackRegionId,
            // Ignored server-side once the feature has a Polygon boundary —
            // only takes effect while there's none to derive area from.
            areaHectares: formAreaHectares ? Number(formAreaHectares) : null,
          }),
        });
        if (!res.ok) throw new Error('Failed to save properties');
        await fetchFeatures();
        setFormOpen(false);
      }
    } catch {
      setError(formMode === 'create' ? 'Failed to create feature' : 'Failed to save properties');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/spatial-features/${deleteTarget.id}`, { method: 'DELETE', headers: authHeader });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to delete feature');
      }
      setFeatures((prev) => prev.filter((f) => f.id !== deleteTarget.id));
      if (selectedFeatureId === deleteTarget.id) setSelectedFeatureId(null);
      setDeleteTarget(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete feature');
    } finally {
      setSaving(false);
    }
  };

  const cancelDraw = () => {
    setDrawMode('none');
    setPendingGeometry(null);
    setPendingCategory(null);
    setPendingGeometryKind('');
    setFormOpen(false);
    setFormMode('create');
    setFormImageFile(null);
    setPendingRegionGeometry(null);
    setRegionFormOpen(false);
  };

  const selectFeatureFromSidebar = (feature: SpatialFeature) => {
    setSelectedRegionId(null);
    setSelectedFeatureId(feature.id);
    setFocusRequest(focusRequestForFeature(feature));
  };

  // Double-clicking any feature switches to the Records tab, scoped to that
  // feature's subtree.
  const openOperationsFor = (feature: SpatialFeature) => {
    setOperationsRoot((prev) => ({ id: feature.id, name: feature.name, key: (prev?.key ?? 0) + 1 }));
    setActiveTab('operations');
  };

  const openContextMenu = (feature: SpatialFeature, mouseX: number, mouseY: number) => {
    setContextMenu({ feature, mouseX, mouseY });
  };

  const recordType = operationTypes.find((t) => t.id === recordTypeId) ?? null;
  const recordMethodOptions = recordType ? operationMethods.filter((m) => recordType.methodIds.includes(m.id)) : [];

  const openRecordDialog = () => {
    if (!contextMenu) return;
    const parent = contextMenu.feature;
    const firstType = operationTypes[0];
    setRecordEditingId(null);
    setRecordParent(parent);
    setRecordTypeId(firstType?.id ?? '');
    setRecordMethodId('');
    setRecordTimeFrom(defaultRangeTo());
    setRecordNotes('');
    setRecordWaterRate('');
    setRecordWaterRateUnitId('');
    setRecordGeometryKind(firstType?.defaultGeometryKind ?? '');
    setRecordGeometry(null);
    setRecordAreaHectares('');
    setRecordBandPercent('');
    setRecordInitialGeometry(null);
    setRecordInitialMappedArea(parent.areaHectares);
    setRecordProducts([]);
    setRecordError(null);
    setRecordDialogOpen(true);
    setContextMenu(null);
  };

  // Opened from the Records tab (a row click) — pre-fills every field from
  // the existing record so handleSaveRecord's edit branch (PUT, not POST)
  // has something to diff against.
  const openRecordEditDialog = (record: RecordItem) => {
    const parent = record.spatialFeatureId ? features.find((f) => f.id === record.spatialFeatureId) ?? null : null;
    const type = operationTypes.find((t) => t.id === record.operationTypeId);
    setRecordEditingId(record.id);
    setRecordEditingItem(record);
    setRecordRegionId(record.regionId);
    setRecordTab('record');
    setRecordParent(parent);
    setRecordTypeId(record.operationTypeId);
    setRecordMethodId(record.operationMethodId ?? '');
    setRecordTimeFrom(toDatetimeLocal(new Date(record.timeFrom)));
    setRecordNotes(record.notes ?? '');
    setRecordWaterRate(record.waterRate != null ? String(record.waterRate) : '');
    setRecordWaterRateUnitId(record.waterRateUnitId ?? '');
    setRecordGeometryKind(record.geometry?.geometryType ?? type?.defaultGeometryKind ?? '');
    setRecordGeometry(record.geometry);
    setRecordAreaHectares(record.areaHectares != null ? String(record.areaHectares) : '');
    setRecordBandPercent(record.bandPercent != null ? String(record.bandPercent) : '');
    setRecordInitialGeometry(record.geometry);
    setRecordInitialMappedArea(record.mappedAreaHectares);
    setRecordEditingRegionLabel(
      record.linkedRegionName ?? (record.regionName ? `not linked to a region (source said "${record.regionName.trim()}")` : null)
    );
    setRecordProducts(
      record.products.map((p) => ({
        productId: p.productId,
        productGroupId: p.productGroupId,
        productName: p.productName,
        rate: String(p.rate),
        rateUnitId: p.rateUnitId,
      }))
    );
    setRecordError(null);
    setRecordDialogOpen(true);
  };

  const selectRecordType = (typeId: string) => {
    const type = operationTypes.find((t) => t.id === typeId);
    setRecordTypeId(typeId);
    setRecordMethodId('');
    setRecordGeometryKind(type?.defaultGeometryKind ?? '');
    setRecordGeometry(null);
  };

  // Mirrors RecordsController: a shape drawn in this dialog is the record's
  // own coverage (its polygon area wins, else the anchor feature's area);
  // an untouched shape keeps whatever the server already derived.
  const recordMappedArea =
    recordGeometry === recordInitialGeometry
      ? recordInitialMappedArea
      : polygonAreaHectares(recordGeometry) ?? recordParent?.areaHectares ?? null;
  const recordAreaInvalid = recordAreaHectares !== '' && !(Number(recordAreaHectares) >= 0);
  const recordBandInvalid = bandInputInvalid(recordBandPercent);

  // Which Region the record is grouped under for regional reporting
  // (Record.RegionId). Editing: whatever it already has. Creating: what
  // RecordsController will inherit — the anchor feature's associated
  // region, else its parent Farm's.
  const inheritedRegionName = (feature: SpatialFeature | null): string | null => {
    let current = feature;
    for (let depth = 0; current && depth < 5; depth++) {
      if (current.fallbackRegionName) return current.fallbackRegionName;
      const parentId = layers.find((l) => l.id === current!.spatialLayerId)?.parentSpatialFeatureId;
      current = parentId ? features.find((f) => f.id === parentId) ?? null : null;
    }
    return null;
  };
  const recordRegionLabel = recordEditingId ? recordEditingRegionLabel : inheritedRegionName(recordParent);

  const cancelRecordDialog = () => {
    setRecordDialogOpen(false);
    setRecordParent(null);
    setRecordEditingId(null);
    setRecordEditingItem(null);
    setRecordTypeId('');
    setRecordMethodId('');
    setRecordGeometry(null);
    setRecordProducts([]);
    setRecordError(null);
    if (returnToRecordsTab) {
      setActiveTab('operations');
      setReturnToRecordsTab(false);
    }
  };

  // Detour into the normal draw-on-map flow for the record's own optional
  // shape — the dialog closes but recordParent/recordType etc. stay set, so
  // handleDrawn's 'record' branch can reopen the dialog with the shape
  // attached once the user finishes drawing.
  const beginRecordDraw = () => {
    if (!recordGeometryKind) return;
    setRecordDialogOpen(false);
    setDrawMode('record');
  };

  const cancelRecordDraw = () => {
    setDrawMode('none');
    setRecordDialogOpen(true);
  };

  const addRecordProductRow = () => {
    setRecordProducts((prev) => [...prev, { productId: null, productGroupId: null, productName: '', rate: '', rateUnitId: '' }]);
  };

  const removeRecordProductRow = (index: number) => {
    setRecordProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveRecord = async () => {
    if (!recordTypeId || !recordTimeFrom) return;
    if (!recordEditingId && !recordParent) return;
    setRecordSaving(true);
    setRecordError(null);
    try {
      const products = recordProducts
        .filter((p) => (p.productId || p.productGroupId) && p.rateUnitId)
        .map((p) => ({ productId: p.productId, productGroupId: p.productGroupId, rate: Number(p.rate) || 0, rateUnitId: p.rateUnitId }));

      const body: Record<string, unknown> = {
        operationTypeId: recordTypeId,
        operationMethodId: recordMethodId || null,
        timeFrom: toIso(recordTimeFrom),
        notes: recordNotes.trim() || null,
        waterRate: recordWaterRate ? Number(recordWaterRate) : null,
        waterRateUnitId: recordWaterRateUnitId || null,
        areaHectares: recordAreaHectares !== '' ? Number(recordAreaHectares) : null,
        bandPercent: recordBandPercent.trim() !== '' ? Number(recordBandPercent) : null,
        products,
      };
      if (recordEditingId) {
        // Only the create flow sets/changes which feature a record is
        // anchored to (via the context-menu's right-clicked feature) — the
        // edit dialog has no UI to re-parent, so spatialFeatureId is left
        // untouched. Geometry needs an explicit clear flag on update (a bare
        // null means "leave as-is", same convention as everywhere else this
        // pattern is used).
        if (recordGeometry) {
          body.geometry = recordGeometry;
        } else {
          body.clearGeometry = true;
        }
        if (!recordMethodId) {
          body.clearOperationMethod = true;
        }
        // Blank = drop the override and fall back to the mapped area.
        if (recordAreaHectares === '') {
          body.clearAreaHectares = true;
        }
        if (recordBandPercent.trim() === '') {
          body.clearBandPercent = true;
        }
        if (recordRegionId !== (recordEditingItem?.regionId ?? null)) {
          if (recordRegionId) {
            body.regionId = recordRegionId;
          } else {
            body.clearRegion = true;
          }
        }
      } else {
        body.spatialFeatureId = recordParent!.id;
        body.geometry = recordGeometry;
      }

      const url = recordEditingId ? `/api/records/${recordEditingId}` : `/api/businesses/${businessId}/records`;
      const res = await fetch(url, {
        method: recordEditingId ? 'PUT' : 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errorBody = await res.json().catch(() => ({}));
        throw new Error(errorBody.error || `Failed to ${recordEditingId ? 'save' : 'create'} record`);
      }
      await fetchRecords();
      setRecordsRefreshKey((k) => k + 1);
      if (recordParent) setFocusRequest(focusRequestForFeature(recordParent));
      cancelRecordDialog();
    } catch (e) {
      setRecordError(e instanceof Error ? e.message : `Failed to ${recordEditingId ? 'save' : 'create'} record`);
    } finally {
      setRecordSaving(false);
    }
  };

  // Children of a feature = features belonging to any layer parented to it.
  const childFeaturesOf = (featureId: string): SpatialFeature[] => {
    const childLayerIds = new Set(layers.filter((l) => l.parentSpatialFeatureId === featureId).map((l) => l.id));
    return features.filter((f) => childLayerIds.has(f.spatialLayerId));
  };

  const toggleFeatureVisibility = (feature: SpatialFeature) => {
    setHiddenFeatureIds((prev) => {
      const next = new Set(prev);
      if (next.has(feature.id)) {
        // Un-hiding just this feature — its ancestors/descendants keep
        // whatever hidden state they already have.
        next.delete(feature.id);
      } else {
        // Hiding cascades down: collect this feature and every descendant.
        const collectIds = (id: string): string[] => {
          const children = childFeaturesOf(id);
          return [id, ...children.flatMap((c) => collectIds(c.id))];
        };
        collectIds(feature.id).forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleRegionVisibility = (regionId: string) => {
    setVisibleRegionIds((prev) => {
      const next = new Set(prev);
      if (next.has(regionId)) next.delete(regionId);
      else next.add(regionId);
      return next;
    });
  };

  const renderFeatureNode = (feature: SpatialFeature, depth: number): React.ReactNode => {
    const children = childFeaturesOf(feature.id);
    const hasChildren = children.length > 0;
    const collapsed = collapsedFeatureIds.has(feature.id);
    return (
      <React.Fragment key={feature.id}>
        <ListItemButton
          selected={selectedFeatureId === feature.id}
          onClick={() => selectFeatureFromSidebar(feature)}
          onDoubleClick={() => openOperationsFor(feature)}
          onContextMenu={(e) => {
            e.preventDefault();
            openContextMenu(feature, e.clientX, e.clientY);
          }}
          sx={{ pl: 1 + depth * 2, py: 0.25, minHeight: 34, gap: 0.5 }}
        >
          {hasChildren ? (
            <IconButton
              size="small"
              sx={{ p: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                toggleCollapsed(feature.id);
              }}
            >
              {collapsed ? <KeyboardArrowRightIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 28, flexShrink: 0 }} />
          )}
          <Checkbox
            size="small"
            checked={!hiddenFeatureIds.has(feature.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleFeatureVisibility(feature)}
            sx={{ p: 0.25 }}
          />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colorFor(feature.id), flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
            {layerOf(feature)?.layerTypeName ?? ''}
          </Typography>
          <Typography variant="body2" noWrap sx={{ flexGrow: 1 }}>{feature.name}</Typography>
          {isAdmin && (
            <IconButton
              size="small"
              sx={{ p: 0.25 }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFeatureId(feature.id);
                openEditProperties(feature);
              }}
            >
              <EditIcon fontSize="inherit" />
            </IconButton>
          )}
        </ListItemButton>
        {hasChildren && !collapsed && children.map((child) => renderFeatureNode(child, depth + 1))}
      </React.Fragment>
    );
  };

  const colorFor = (featureId: string) => {
    const feature = features.find((f) => f.id === featureId);
    if (feature?.colour) return feature.colour;
    const layer = layerOf(feature);
    if (layer?.colour) return layer.colour;
    const layerType = layerTypes.find((t) => t.id === layer?.layerTypeId);
    if (layerType?.defaultColour) return layerType.defaultColour;
    const idx = features.findIndex((f) => f.id === featureId);
    return LAYER_COLORS[idx % LAYER_COLORS.length];
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
      <style>{`
        .zone-label {
          background: transparent;
          border: none;
          box-shadow: none;
          font-weight: 600;
          font-size: 0.8rem;
          color: #fff;
          text-shadow: 0 1px 3px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,0.9);
          pointer-events: none;
        }
        .zone-label::before {
          display: none;
        }
      `}</style>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ minHeight: 36, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tab value="map" label="Map" sx={{ minHeight: 36, py: 0.5 }} />
        <Tab value="operations" label="Records" sx={{ minHeight: 36, py: 0.5 }} />
        <Tab value="other" label="Other Records" sx={{ minHeight: 36, py: 0.5 }} />
      </Tabs>

      {/* Hidden rather than unmounted on the Map tab, so its sort, page and
          filters are still there when an edit returns to it. Takes the
          panel's place in this column layout (fill the rest, scroll
          inside) — without that the table outgrows the page and the
          paging gets clipped. */}
      <Box sx={{ display: activeTab === 'operations' ? 'flex' : 'none', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
        <OperationsPanel
          businessId={businessId}
          authHeader={authHeader}
          isAdmin={isAdmin}
          rootFeatureId={operationsRoot?.id ?? null}
          rootFeatureName={operationsRoot?.name ?? null}
          rootFeatureKey={operationsRoot?.key}
          refreshKey={recordsRefreshKey}
          onSelectRecord={(record) => {
            setReturnToRecordsTab(true);
            setActiveTab('map');
            if (record.spatialFeatureId) {
              const feature = features.find((f) => f.id === record.spatialFeatureId);
              setSelectedFeatureId(record.spatialFeatureId);
              if (feature) setFocusRequest(focusRequestForFeature(feature));
            } else if (record.geometry) {
              setFocusRequest(focusRequestForFeature({ geometry: record.geometry }));
            }
            openRecordEditDialog(record);
          }}
        />
      </Box>

      {/* Field seasons — what an import fills without creating a Record. */}
      {activeTab === 'other' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
          <FieldSeasonsPanel businessId={businessId} authHeader={authHeader} isAdmin={isAdmin} />
        </Box>
      )}

      {activeTab === 'map' && (
      <>
      {isAdmin && (
        <Paper
          square
          elevation={0}
          sx={{
            p: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
            overflowX: { xs: 'auto', sm: 'visible' },
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, flexShrink: 0 }}>
            Add{selectedFeature ? ` under "${selectedFeature.name}"` : ''}:
          </Typography>
          {CATEGORY_BAR_ORDER.map((category) => (
            <Button
              key={category}
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={drawMode !== 'none' || !allowedChildCategories.includes(category)}
              onClick={() => startCreateForCategory(selectedFeatureId, category)}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              {CATEGORY_LABELS[category]}
            </Button>
          ))}
        </Paper>
      )}

      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
      <Box
        sx={{
          ...(isMobile
            ? {
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                zIndex: 1200,
                width: 280,
                maxWidth: '85vw',
                bgcolor: 'background.paper',
                boxShadow: 6,
                transform: mobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
                transition: 'transform 0.2s ease',
              }
            : { width: sidebarWidth, flexShrink: 0 }),
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isMobile && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 0.5, pt: 0.5 }}>
            <IconButton size="small" onClick={() => setMobileSidebarOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        <Tabs value={sidebarTab} onChange={(_, v) => setSidebarTab(v)} variant="fullWidth" sx={{ minHeight: 36 }}>
          <Tab value="layers" label={`Layers (${features.length + regions.length})`} sx={{ minHeight: 36, py: 0.5 }} />
          <Tab value="devices" label={`Devices (${devices.length})`} sx={{ minHeight: 36, py: 0.5 }} />
        </Tabs>

        {sidebarTab === 'layers' && (
          <>
            <Accordion
              disableGutters
              square
              expanded={businessLayersExpanded}
              onChange={(_, expanded) => setBusinessLayersExpanded(expanded)}
              sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ px: 1.5, minHeight: 40, '& .MuiAccordionSummary-content': { alignItems: 'center', justifyContent: 'space-between', my: 0.5 } }}>
                <Typography variant="overline" color="text.secondary">Business Layers ({features.length})</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                  <IconButton size="small" onClick={() => { fetchLayers(); fetchFeatures(); }}><RefreshIcon fontSize="small" /></IconButton>
                  {isAdmin && (
                    <>
                      <IconButton size="small" onClick={() => setTypesDialogOpen(true)} title="Manage Types">
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="primary" onClick={() => startCreate(null)} disabled={drawMode !== 'none'}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {drawMode === 'picking' && (
                  <Box sx={{ mx: 1.5, mb: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                      <InputLabel id="layer-type-label">Type</InputLabel>
                      <Select
                        labelId="layer-type-label"
                        label="Type"
                        value={pendingTypeId}
                        onChange={(e) => {
                          const type = layerTypes.find((t) => t.id === e.target.value);
                          setPendingTypeId(e.target.value);
                          setPendingGeometryKind(type?.defaultGeometryKind ?? '');
                        }}
                      >
                        {layerTypes
                          .filter((t) => !pendingCategory || t.category === pendingCategory)
                          .map((t) => (
                            <MenuItem key={t.id} value={t.id}>{t.name} ({t.allowedGeometryKinds.join(' or ')})</MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    {pendingType && pendingType.allowedGeometryKinds.length > 1 && (
                      <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                        <InputLabel id="layer-shape-label">Shape</InputLabel>
                        <Select
                          labelId="layer-shape-label"
                          label="Shape"
                          value={pendingGeometryKind}
                          onChange={(e) => setPendingGeometryKind(e.target.value as GeometryKind)}
                        >
                          {pendingType.allowedGeometryKinds.map((kind) => (
                            <MenuItem key={kind} value={kind}>
                              {kind === 'LineString' ? 'Centre-line path' : kind === 'Polygon' ? 'Swath / coverage area' : kind}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button size="small" onClick={cancelDraw}>Cancel</Button>
                      <Button size="small" variant="contained" onClick={beginDrawing} disabled={!pendingTypeId || !pendingGeometryKind}>Draw on map</Button>
                    </Box>
                  </Box>
                )}

                {(drawMode === 'create' || drawMode === 'redraw') && (
                  <Alert severity="info" sx={{ mx: 1.5, mb: 1 }} action={<Button size="small" onClick={cancelDraw}>Cancel</Button>}>
                    {drawMode === 'create' ? `Draw the new ${pendingType?.name}'s shape on the map.` : 'Draw the new shape on the map.'}
                  </Alert>
                )}

                {drawMode === 'record' && (
                  <Alert severity="info" sx={{ mx: 1.5, mb: 1 }} action={<Button size="small" onClick={cancelRecordDraw}>Cancel</Button>}>
                    Draw this operation's shape on the map.
                  </Alert>
                )}

                {error && <Alert severity="error" sx={{ mx: 1.5, mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

                {loadingLayers ? (
                  <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>
                ) : features.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No spatial features yet for {businessName}.
                  </Typography>
                ) : (
                  <List dense>
                    {features
                      .filter((f) => {
                        const layer = layerOf(f);
                        return layer && layer.parentSpatialFeatureId === null;
                      })
                      .map((f) => renderFeatureNode(f, 0))}
                  </List>
                )}

                {selectedFeature && drawMode === 'none' && (
                  <>
                    <Divider sx={{ mt: 'auto' }} />
                    <Box sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: colorFor(selectedFeature.id), flexShrink: 0 }} />
                        <Typography variant="subtitle2">{selectedFeature.name}</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {selectedFeatureLayer?.layerTypeName}
                      </Typography>
                      {selectedFeature.deviceName && <Chip size="small" label={selectedFeature.deviceName} sx={{ mt: 0.5 }} />}
                      {selectedFeature.notes && <Typography variant="body2" sx={{ mt: 1 }}>{selectedFeature.notes}</Typography>}

                      {selectedFeature.geometry?.geometryType === 'Polygon' && !selectedFeature.image && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <SatellitePanel
                            key={selectedFeature.id}
                            featureId={selectedFeature.id}
                            featureName={selectedFeature.name}
                            onShowImage={(image) => satelliteImagery.addOverlay(selectedFeature.id, selectedFeature.name, image)}
                          />
                        </>
                      )}

                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Intersects</Typography>
                      {loadingIntersects ? (
                        <CircularProgress size={16} />
                      ) : intersects.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">No intersecting features.</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {intersects.map((i) => (
                            <Chip key={i.id} size="small" label={`${i.name} (${i.layerType})`} onClick={() => setSelectedFeatureId(i.id)} />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion
              disableGutters
              square
              expanded={globalLayersExpanded}
              onChange={(_, expanded) => setGlobalLayersExpanded(expanded)}
              sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ px: 1.5, minHeight: 40, '& .MuiAccordionSummary-content': { alignItems: 'center', justifyContent: 'space-between', my: 0.5 } }}>
                <Typography variant="overline" color="text.secondary">Global Layers ({regions.length})</Typography>
                {isAdmin && (
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => { e.stopPropagation(); startRegionCreate(); }}
                    disabled={drawMode !== 'none'}
                    title="Draw New Region"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                {(drawMode === 'region-create' || drawMode === 'region-redraw') && (
                  <Alert severity="info" sx={{ mx: 1.5, mb: 1, mt: 0.5 }} action={<Button size="small" onClick={cancelDraw}>Cancel</Button>}>
                    {drawMode === 'region-create' ? "Draw the new region's boundary on the map." : 'Draw the new boundary on the map.'}
                  </Alert>
                )}

                <List dense>
                  <ListItemButton sx={{ py: 0.25, gap: 0.5 }} onClick={() => setShowQldCadastre((v) => !v)}>
                    <Checkbox
                      size="small"
                      checked={showQldCadastre}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => setShowQldCadastre((v) => !v)}
                      sx={{ p: 0.25 }}
                    />
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ffca28', flexShrink: 0 }} />
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>Live</Typography>
                    <Typography variant="body2" noWrap>QLD Cadastre (DCDB)</Typography>
                  </ListItemButton>
                </List>
                <Divider sx={{ mb: 0.5 }} />
                {regions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No reference regions loaded.</Typography>
                ) : (
                  regionsByType.map(([regionType, list]) => (
                    <Accordion
                      key={regionType}
                      disableGutters
                      square
                      expanded={expandedRegionTypes.has(regionType)}
                      onChange={() => toggleRegionTypeExpanded(regionType)}
                      sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ pl: 2.5, pr: 1.5, minHeight: 34, '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 0.75 } }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: regionColour(regionType), flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary">{regionType} ({list.length})</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        <List dense>
                          {list.map((r) => (
                            <ListItemButton
                              key={r.id}
                              selected={selectedRegionId === r.id}
                              sx={{ py: 0.25, pl: 3, gap: 0.5 }}
                              onClick={() => selectRegionFromSidebar(r)}
                            >
                              <Checkbox
                                size="small"
                                checked={visibleRegionIds.has(r.id)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => toggleRegionVisibility(r.id)}
                                sx={{ p: 0.25 }}
                              />
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.colour || regionColour(r.regionType), flexShrink: 0 }} />
                              <Typography variant="body2" noWrap sx={{ flexGrow: 1 }}>{r.name}</Typography>
                              {isAdmin && (
                                <IconButton
                                  size="small"
                                  sx={{ p: 0.25 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedRegionId(r.id);
                                    openRegionProperties(r);
                                  }}
                                >
                                  <EditIcon fontSize="inherit" />
                                </IconButton>
                              )}
                            </ListItemButton>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  ))
                )}
              </AccordionDetails>
            </Accordion>

            {satelliteImagery.overlays.length > 0 && (
              <Accordion disableGutters square defaultExpanded sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ px: 1.5, minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                  <Typography variant="overline" color="text.secondary">Satellite Imagery ({satelliteImagery.overlays.length})</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <SatelliteImageryControls
                    overlays={satelliteImagery.overlays}
                    onUpdate={satelliteImagery.updateOverlay}
                    onStretch={satelliteImagery.setStretch}
                    onRemove={satelliteImagery.removeOverlay}
                  />
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}

        {sidebarTab === 'devices' && (
          <>
            <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
              <IconButton size="small" onClick={fetchDevices}><RefreshIcon fontSize="small" /></IconButton>
            </Box>
            {loadingDevices ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress size={24} /></Box>
            ) : devices.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No devices with location data yet.
                </Typography>
                {isAdmin && (
                  <Button size="small" variant="outlined" onClick={() => setSetLocationOpen(true)}>
                    Set Device Location
                  </Button>
                )}
              </Box>
            ) : (
              <List dense>
                {devices.map((d) => (
                  <ListItemButton
                    key={d.id}
                    selected={focusedDeviceId === d.id}
                    onClick={() => {
                      setFocusedDeviceId(d.id);
                      if (isMobile) setMobileSidebarOpen(false);
                    }}
                  >
                    <ListItemText primary={d.name} secondary={d.deviceTypeName} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </>
        )}

      </Box>

      {!isMobile && (
        <Box
          onMouseDown={handleResizeMouseDown}
          sx={{
            width: 4,
            flexShrink: 0,
            cursor: 'col-resize',
            bgcolor: 'divider',
            '&:hover': { bgcolor: 'primary.main' },
          }}
        />
      )}

      {isMobile && (
        <IconButton
          size="small"
          onClick={() => setMobileSidebarOpen(true)}
          sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1100, bgcolor: 'background.paper', boxShadow: 2 }}
        >
          <LayersIcon fontSize="small" />
        </IconButton>
      )}

      <IconButton
        size="small"
        onClick={toggleBaseLayer}
        title={baseLayer === 'street' ? 'Switch to satellite' : 'Switch to street map'}
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1100, bgcolor: 'background.paper', boxShadow: 2 }}
      >
        {baseLayer === 'street' ? <SatelliteAltIcon fontSize="small" /> : <MapIcon fontSize="small" />}
      </IconButton>

      <Box sx={{ flexGrow: 1, minWidth: 0, minHeight: 0 }}>
        <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: '100%', width: '100%' }}>
          <MapCenterController center={mapCenter} zoom={mapZoom} />
          <MapFocusController request={focusRequest} />
          {/* Suppressed entirely while vertex-editing — every mousemove during
              a drag would otherwise re-render this whole component, and even
              with positions memoized, react-leaflet's own prop reconciliation
              (or any other prop object recreated inline on the edited layer)
              risks fighting Leaflet's native drag handling. Simplest robust
              fix: don't re-render at all during the drag, not just avoid
              resetting positions once we do. */}
          <MapMouseTracker onMove={editingFeatureId ? noopMouseMove : setCursorLatLng} />
          <MapViewPersister businessId={businessId} />
          {baseLayer === 'satellite' ? (
            <TileLayer
              attribution="Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community"
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          ) : (
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          )}
          <SatelliteImageryLayer overlays={satelliteImagery.overlays} />
          {showQldCadastre && (
            <WMSTileLayer
              url="https://spatial-gis.information.qld.gov.au/arcgis/services/PlanningCadastre/LandParcelPropertyFramework/MapServer/WmsServer"
              layers="Cadastral_parcels13960"
              format="image/png"
              transparent
              version="1.3.0"
              attribution="Cadastral data &copy; State of Queensland (Department of Resources)"
            />
          )}
          {regions
            .filter((r) => visibleRegionIds.has(r.id) && !(drawMode === 'region-redraw' && r.id === selectedRegionId))
            .map((region) => (
              <GeoJSON
                key={region.id}
                data={region.geometry}
                style={{ color: region.colour || regionColour(region.regionType), weight: 2, fillOpacity: region.fillOpacity ?? 0.03, dashArray: '6 4' }}
                interactive={drawMode === 'none'}
              >
                <Tooltip sticky>{region.name}</Tooltip>
                <Popup>
                  <Typography variant="subtitle2">{region.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{region.regionType}</Typography>
                </Popup>
              </GeoJSON>
            ))}
          {devices.map((d) => (
            <Marker key={`device-${d.id}`} position={[d.latitude, d.longitude]}>
              <Popup>
                <Box sx={{ minWidth: 160 }}>
                  <Typography variant="subtitle2">{d.name}</Typography>
                  {d.deviceTypeName && (
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>{d.deviceTypeName}</Typography>
                  )}
                  {d.childCount > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {d.childCount} child device{d.childCount !== 1 ? 's' : ''}
                    </Typography>
                  )}
                  <Button variant="contained" size="small" fullWidth sx={{ mt: 1 }} onClick={() => navigate(`/devices/${d.id}/events`)}>
                    View Details
                  </Button>
                </Box>
              </Popup>
            </Marker>
          ))}
          {features.map((feature) => {
            // While this feature is being vertex-edited, use the frozen
            // snapshot taken at edit-start instead of the live-computed
            // geometry — an unrelated re-render (anything that touches
            // component state) would otherwise recompute positions from
            // unchanged source data and stomp the user's in-progress drag,
            // since Leaflet's editing handler mutates the instance directly,
            // outside this state.
            const geometry = feature.id === editingFeatureId && editSnapshot ? editSnapshot : feature.geometry;
            if (!geometry || geometry.points.length === 0) return null;
            if (hiddenFeatureIds.has(feature.id)) return null;
            if (drawMode === 'redraw' && feature.id === selectedFeatureId) return null;
            const color = colorFor(feature.id);
            const selected = selectedFeatureId === feature.id;
            const layerTypeName = layerOf(feature)?.layerTypeName ?? '';
            // While actively drawing (create/redraw/record), other features
            // must not intercept map clicks — Leaflet paths are interactive
            // by default and would otherwise swallow clicks meant for
            // leaflet-draw (accidentally selecting the feature underneath
            // instead of placing a vertex).
            const isDrawing = drawMode === 'create' || drawMode === 'redraw' || drawMode === 'record' || drawMode === 'region-create' || drawMode === 'region-redraw';

            if (feature.image && geometry.geometryType === 'Polygon') {
              const objectUrl = imageObjectUrls.get(feature.id);
              if (!objectUrl) return null; // still fetching the blob
              const lats = geometry.points.map((p) => p.lat);
              const lngs = geometry.points.map((p) => p.lng);
              const bounds: [[number, number], [number, number]] = [
                [Math.min(...lats), Math.min(...lngs)],
                [Math.max(...lats), Math.max(...lngs)],
              ];
              return (
                <ImageOverlay
                  key={feature.id}
                  url={objectUrl}
                  bounds={bounds}
                  opacity={selected ? 1 : 0.9}
                  interactive={!isDrawing}
                  eventHandlers={{
                    click: () => setSelectedFeatureId(feature.id),
                    dblclick: () => openOperationsFor(feature),
                    contextmenu: (e) => {
                      e.originalEvent.preventDefault();
                      openContextMenu(feature, e.originalEvent.clientX, e.originalEvent.clientY);
                    },
                  }}
                />
              );
            }

            if (geometry.geometryType === 'Point') {
              const p = geometry.points[0];
              return (
                <Marker
                  key={feature.id}
                  position={[p.lat, p.lng]}
                  icon={layerPointIcon(color)}
                  interactive={!isDrawing}
                  eventHandlers={{
                    click: () => setSelectedFeatureId(feature.id),
                    dblclick: () => openOperationsFor(feature),
                    contextmenu: (e) => {
                      e.originalEvent.preventDefault();
                      openContextMenu(feature, e.originalEvent.clientX, e.originalEvent.clientY);
                    },
                  }}
                >
                  <Tooltip permanent direction="top" className="zone-label">{feature.name}</Tooltip>
                  <Popup>
                    <Typography variant="subtitle2">{feature.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{layerTypeName}</Typography>
                  </Popup>
                </Marker>
              );
            }

            const positions = positionsByFeatureId.get(feature.id) ?? [];

            if (geometry.geometryType === 'LineString') {
              return (
                <Polyline
                  key={feature.id}
                  ref={(instance) => {
                    if (instance) featureRefs.current.set(feature.id, instance);
                    else featureRefs.current.delete(feature.id);
                    enableEditingOnInstance(feature.id, instance);
                  }}
                  positions={positions}
                  pathOptions={{ color, weight: selected ? 4 : 3 }}
                  interactive={!isDrawing}
                  eventHandlers={{
                    click: () => setSelectedFeatureId(feature.id),
                    dblclick: () => openOperationsFor(feature),
                    contextmenu: (e) => {
                      e.originalEvent.preventDefault();
                      openContextMenu(feature, e.originalEvent.clientX, e.originalEvent.clientY);
                    },
                  }}
                >
                  <Tooltip permanent direction="center" className="zone-label">{feature.name}</Tooltip>
                  <Popup>
                    <Typography variant="subtitle2">{feature.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{layerTypeName}</Typography>
                  </Popup>
                </Polyline>
              );
            }

            return (
              <Polygon
                key={feature.id}
                ref={(instance) => {
                  if (feature.id === editingFeatureId) {
                    // TEMPORARY diagnostic — if this logs more than once
                    // during a single edit session, the layer is being
                    // remounted mid-edit. enableEditingOnInstance below
                    // re-syncs editing state onto whichever instance is
                    // current when that happens.
                    console.log('[vertex-edit] Polygon ref callback fired for edited feature', feature.id, instance ? 'SET' : 'CLEARED');
                  }
                  if (instance) featureRefs.current.set(feature.id, instance);
                  else featureRefs.current.delete(feature.id);
                  enableEditingOnInstance(feature.id, instance);
                }}
                positions={positions}
                pathOptions={{
                  color,
                  fillOpacity: overSatelliteImage(geometry.points) ? 0 : feature.fillOpacity ?? (selected ? 0.4 : 0.15),
                  weight: selected ? 3 : 2,
                }}
                interactive={!isDrawing}
                eventHandlers={{
                  click: () => setSelectedFeatureId(feature.id),
                  dblclick: () => openOperationsFor(feature),
                  contextmenu: (e) => {
                    e.originalEvent.preventDefault();
                    openContextMenu(feature, e.originalEvent.clientX, e.originalEvent.clientY);
                  },
                }}
              >
                <Tooltip permanent direction="center" className="zone-label">{feature.name}</Tooltip>
                <Popup>
                  <Typography variant="subtitle2">{feature.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{layerTypeName}</Typography>
                </Popup>
              </Polygon>
            );
          })}
          {isAdmin && pendingType && pendingGeometryKind && (
            <ZoneDrawControl
              active={drawMode === 'create' || drawMode === 'redraw'}
              shapeType={pendingGeometryKind}
              useRectangleTool={isImageOverlayType}
              onDrawn={handleDrawn}
            />
          )}
          {isAdmin && drawMode === 'record' && recordGeometryKind && (
            <ZoneDrawControl active shapeType={recordGeometryKind} onDrawn={handleDrawn} />
          )}
          {isAdmin && (drawMode === 'region-create' || drawMode === 'region-redraw') && (
            <ZoneDrawControl active shapeType="Polygon" onDrawn={handleDrawn} />
          )}
        </MapContainer>
      </Box>
      </Box>

      <Paper
        square
        elevation={0}
        sx={{ px: { xs: 1, sm: 1.5 }, py: 0.5, display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 3 }, flexWrap: 'wrap', borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Box sx={{ flexGrow: 1 }} />

        {satelliteReadout && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
            {satelliteReadout}
          </Typography>
        )}

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: 'monospace', display: { xs: 'none', sm: 'block' } }}
        >
          {cursorLatLng ? `Lat ${cursorLatLng.lat.toFixed(5)}, Lng ${cursorLatLng.lng.toFixed(5)}` : 'Lat —, Lng —'}
        </Typography>
      </Paper>
      </>
      )}

      {/* Create/edit feature dialog — shown after a new shape is drawn, or via "Edit Properties" */}
      <Dialog open={formOpen} onClose={cancelDraw} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{formMode === 'create' ? `New ${pendingType?.name}` : `Edit ${selectedFeature?.name}`}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {formMode === 'edit' && selectedFeature && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {allowedChildCategories.length > 0 && (
                  <Button size="small" startIcon={<AddIcon />} onClick={() => { setFormOpen(false); startCreate(selectedFeature.id); }}>Add Child</Button>
                )}
                <Button size="small" startIcon={<EditIcon />} onClick={() => { setFormOpen(false); startRedraw(selectedFeature); }}>Redraw boundary</Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => { setFormOpen(false); setDeleteTarget(selectedFeature); }}>
                  Delete
                </Button>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Name" value={formName} onChange={(e) => setFormName(e.target.value)} autoFocus fullWidth required />
              <TextField
                label="Colour"
                type="color"
                value={formColour}
                onChange={(e) => setFormColour(e.target.value)}
                sx={{ width: 72, flexShrink: 0 }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PaletteIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>Transparency</Typography>
              <Slider
                size="small"
                value={Math.round((1 - formOpacity) * 100)}
                onChange={(_, v) => setFormOpacity(1 - (v as number) / 100)}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}%`}
              />
            </Box>
            {formMode === 'edit' && selectedFeature?.geometry && (
              <Typography variant="body2" color="text.secondary">
                Area: <strong>{selectedFeature.areaHectares != null ? `${selectedFeature.areaHectares} ha` : '—'}</strong> (from its drawn boundary)
              </Typography>
            )}
            {formMode === 'edit' && !selectedFeature?.geometry && (
              <TextField
                label="Area (ha)"
                type="number"
                value={formAreaHectares}
                onChange={(e) => setFormAreaHectares(e.target.value)}
                inputProps={{ step: 0.01, min: 0 }}
                helperText="No boundary drawn yet — enter the area by hand. Replaced automatically once one is drawn."
                fullWidth
              />
            )}
            {formMode === 'edit' && selectedFeatureLayer?.category === 'Farm' && !selectedFeature?.geometry && (
              <FormControl fullWidth>
                <InputLabel id="fallback-region-label">Associate Region (until boundary is drawn)</InputLabel>
                <Select
                  labelId="fallback-region-label"
                  label="Associate Region (until boundary is drawn)"
                  value={formFallbackRegionId}
                  onChange={(e) => setFormFallbackRegionId(e.target.value)}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  {regions.map((r) => (
                    <MenuItem key={r.id} value={r.id}>{r.name} ({r.regionType})</MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, px: 1.75 }}>
                  This farm has no drawn boundary yet. Imported records for it will use this Region's shape
                  spatially until a real boundary is drawn.
                </Typography>
              </FormControl>
            )}
            {formMode === 'create' && isImageOverlayType && (
              <Box>
                <Button variant="outlined" component="label" size="small">
                  {formImageFile ? formImageFile.name : 'Choose Image…'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    hidden
                    onChange={(e) => setFormImageFile(e.target.files?.[0] ?? null)}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  JPG or PNG, up to 20MB. Placed to fill the rectangle just drawn.
                </Typography>
              </Box>
            )}
            <FormControl fullWidth>
              <InputLabel id="feature-device-label">Device (Optional)</InputLabel>
              <Select
                labelId="feature-device-label"
                label="Device (Optional)"
                value={formDeviceId}
                onChange={(e) => setFormDeviceId(e.target.value)}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {devices.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Notes (optional)"
              value={formNotes}
              onChange={(e) => setFormNotes(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />

            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Attributes (e.g. Irrigation Type, Serial Number, Install Date)
              </Typography>
              {formAttributes.map((row, i) => (
                <Box key={i} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexGrow: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Name (e.g. Irrigation Type)"
                      value={row.key}
                      onChange={(e) => setFormAttributes((prev) => prev.map((r, idx) => (idx === i ? { ...r, key: e.target.value } : r)))}
                      fullWidth
                    />
                    <TextField
                      size="small"
                      placeholder="Value (e.g. Irrigated)"
                      value={row.value}
                      onChange={(e) => setFormAttributes((prev) => prev.map((r, idx) => (idx === i ? { ...r, value: e.target.value } : r)))}
                      fullWidth
                    />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => setFormAttributes((prev) => prev.filter((_, idx) => idx !== i))}
                    sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" startIcon={<AddIcon />} onClick={() => setFormAttributes((prev) => [...prev, { key: '', value: '' }])}>
                Add attribute
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDraw} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleFormSave}
            variant="contained"
            disabled={saving || !formName.trim() || (formMode === 'create' && isImageOverlayType && !formImageFile)}
          >
            {saving ? 'Saving…' : formMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Feature</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={saving}>Cancel</Button>
          <Button onClick={handleDeleteConfirmed} color="error" variant="contained" disabled={saving}>
            {saving ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={regionFormOpen} onClose={closeRegionForm} maxWidth="sm" fullWidth>
        <DialogTitle>{regionFormMode === 'create' ? 'New Region' : 'Region Properties'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {regionFormMode === 'edit' && regionEditTarget && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                <Button size="small" startIcon={<EditIcon />} onClick={() => { closeRegionForm(); startRegionRedraw(regionEditTarget); }}>
                  Redraw boundary
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => { setRegionFormOpen(false); setDeleteRegionTarget(regionEditTarget); }}>
                  Delete
                </Button>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Name" value={regionFormName} onChange={(e) => setRegionFormName(e.target.value)} fullWidth required />
              <TextField
                label="Colour"
                type="color"
                value={regionFormColour}
                onChange={(e) => setRegionFormColour(e.target.value)}
                sx={{ width: 72, flexShrink: 0 }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PaletteIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>Transparency</Typography>
              <Slider
                size="small"
                value={Math.round((1 - regionFormOpacity) * 100)}
                onChange={(_, v) => setRegionFormOpacity(1 - (v as number) / 100)}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v}%`}
              />
            </Box>
            <TextField label="Region Type" value={regionFormType} onChange={(e) => setRegionFormType(e.target.value)} fullWidth required />
            <FormControl fullWidth size="small">
              <InputLabel id="region-parent-label">Parent Region</InputLabel>
              <Select
                labelId="region-parent-label"
                label="Parent Region"
                value={regionFormParentId}
                onChange={(e) => setRegionFormParentId(e.target.value)}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {regions
                  .filter((r) => r.id !== regionEditTarget?.id)
                  .map((r) => (
                    <MenuItem key={r.id} value={r.id}>{r.name} ({r.regionType})</MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRegionForm} disabled={savingRegion}>Cancel</Button>
          <Button
            onClick={handleRegionFormSave}
            variant="contained"
            disabled={savingRegion || !regionFormName.trim() || !regionFormType.trim()}
          >
            {savingRegion ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteRegionTarget} onClose={() => setDeleteRegionTarget(null)}>
        <DialogTitle>Delete Region</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{deleteRegionTarget?.name}</strong> as a Global Layer? This is a shared reference
            boundary, not specific to this business — it stops appearing for every business immediately.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRegionTarget(null)} disabled={deletingRegion}>Cancel</Button>
          <Button onClick={handleDeleteRegionConfirmed} color="error" variant="contained" disabled={deletingRegion}>
            {deletingRegion ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <SetDeviceLocationModal
        open={setLocationOpen}
        onClose={() => setSetLocationOpen(false)}
        businessId={businessId}
        businessName={businessName}
        defaultCenter={mapCenter}
        onSaved={fetchDevices}
      />

      <ManageTypesDialog open={typesDialogOpen} onClose={() => setTypesDialogOpen(false)} authHeader={authHeader} onChanged={fetchLayerTypes} />

      {isAdmin && (
        <Menu
          open={!!contextMenu}
          onClose={() => setContextMenu(null)}
          anchorReference="anchorPosition"
          anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
        >
          <MenuItem disabled={!contextMenu} onClick={openRecordDialog}>
            Add Operation{contextMenu ? ` to ${contextMenu.feature.name}` : ''}
          </MenuItem>
          <MenuItem
            disabled={
              !contextMenu ||
              !!editingFeatureId ||
              drawMode !== 'none' ||
              (contextMenu.feature.geometry?.geometryType !== 'Polygon' && contextMenu.feature.geometry?.geometryType !== 'LineString')
            }
            onClick={() => {
              if (contextMenu) startVertexEdit(contextMenu.feature);
              setContextMenu(null);
            }}
          >
            Edit Points
          </MenuItem>
        </Menu>
      )}

      {editingFeatureId && (
        <Alert
          severity="info"
          sx={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 1200 }}
          action={
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button size="small" onClick={cancelVertexEdit}>Cancel</Button>
              <Button size="small" variant="contained" onClick={saveVertexEdit} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          }
        >
          Drag the points to reshape {features.find((f) => f.id === editingFeatureId)?.name}.
        </Alert>
      )}

      {/* Add Operation — a Record, not a SpatialLayer/SpatialFeature. Always
          anchored to recordParent (the right-clicked feature) via
          spatialFeatureId; its own shape is optional (drawn via the "record"
          drawMode detour), and product/rate line items are relational
          (RecordProduct), not free-form attributes, so they can be
          queried/aggregated later. */}
      <Dialog open={recordDialogOpen} onClose={cancelRecordDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {recordEditingId ? `Edit Operation${recordParent ? ` — ${recordParent.name}` : ''}` : `Add Operation to ${recordParent?.name}`}
        </DialogTitle>
        <DialogContent>
          {recordEditingId && recordImportRows.length > 0 && (
            <Tabs value={recordTab} onChange={(_, v) => setRecordTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Tab value="record" label="Record" />
              <Tab value="imported" label={`Imported${recordImportRows.length > 1 ? ` (${recordImportRows.length} rows)` : ''}`} />
            </Tabs>
          )}
          {recordTab === 'imported' && recordImportRows.length > 0 ? (
            <Box sx={{ mt: 1 }}>
              <RecordImportDetails rows={recordImportRows} loading={recordImportLoading} record={recordEditingItem} />
            </Box>
          ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {recordError && <Alert severity="error" onClose={() => setRecordError(null)}>{recordError}</Alert>}

            {recordEditingId ? (
              <Autocomplete
                size="small"
                options={regions}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={regions.find((r) => r.id === recordRegionId) || null}
                onChange={(_, value) => setRecordRegionId(value?.id || null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Region"
                    helperText={
                      !recordEditingItem?.regionId && recordEditingItem?.regionName
                        ? `Not linked — the source said "${recordEditingItem.regionName.trim()}"`
                        : undefined
                    }
                  />
                )}
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                Region: {recordRegionLabel ?? 'none — associate a region with this farm/field to group it regionally'}
              </Typography>
            )}

            <FormControl size="small" fullWidth>
              <InputLabel id="record-type-label">Operation Type</InputLabel>
              <Select labelId="record-type-label" label="Operation Type" value={recordTypeId} onChange={(e) => selectRecordType(e.target.value)}>
                {operationTypes.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth disabled={recordMethodOptions.length === 0}>
              <InputLabel id="record-method-label">Method (optional)</InputLabel>
              <Select labelId="record-method-label" label="Method (optional)" value={recordMethodId} onChange={(e) => setRecordMethodId(e.target.value)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {recordMethodOptions.map((m) => (
                  <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="datetime-local"
              label="When"
              value={recordTimeFrom}
              onChange={(e) => setRecordTimeFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            {recordType && recordType.allowedGeometryKinds.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {recordType.allowedGeometryKinds.length > 1 && !recordGeometry && (
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="record-shape-kind-label">Shape</InputLabel>
                    <Select
                      labelId="record-shape-kind-label"
                      label="Shape"
                      value={recordGeometryKind}
                      onChange={(e) => setRecordGeometryKind(e.target.value as GeometryKind)}
                    >
                      {recordType.allowedGeometryKinds.map((kind) => (
                        <MenuItem key={kind} value={kind}>
                          {kind === 'LineString' ? 'Centre-line path' : kind === 'Polygon' ? 'Swath / coverage area' : kind}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {recordGeometry ? (
                  <>
                    <Chip size="small" color="success" label={`Shape drawn (${recordGeometry.geometryType})`} />
                    <Button size="small" onClick={beginRecordDraw}>Redraw</Button>
                    <Button size="small" onClick={() => setRecordGeometry(null)}>Remove</Button>
                  </>
                ) : (
                  <Button size="small" variant="outlined" onClick={beginRecordDraw} disabled={!recordGeometryKind}>
                    Draw a shape (optional)
                  </Button>
                )}
              </Box>
            )}

            <TextField
              size="small"
              label="Area (ha)"
              type="number"
              value={recordAreaHectares}
              onChange={(e) => setRecordAreaHectares(e.target.value)}
              placeholder={recordMappedArea != null ? String(recordMappedArea) : undefined}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 0, step: 'any' }}
              error={recordAreaInvalid}
              helperText={
                recordAreaInvalid
                  ? 'Must be a positive number'
                  : recordAreaHectares === ''
                    ? recordMappedArea != null
                      ? `Using the mapped area (${recordMappedArea} ha) — enter a value to override it`
                      : 'No mapped area yet — draw a polygon, or enter the treated area'
                    : recordMappedArea != null
                      ? `Overrides the mapped area (${recordMappedArea} ha) — clear to use it instead`
                      : 'No mapped area to compare against yet'
              }
              fullWidth
            />

            <TextField
              size="small"
              label="Band % (optional)"
              type="number"
              value={recordBandPercent}
              onChange={(e) => setRecordBandPercent(e.target.value)}
              inputProps={{ min: 0, max: 100, step: 'any' }}
              error={recordBandInvalid}
              helperText={recordBandInvalid ? 'Must be above 0 and at most 100' : 'Share of the area treated. Blank is all of it'}
              fullWidth
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                size="small"
                label="Water Rate (optional)"
                type="number"
                value={recordWaterRate}
                onChange={(e) => setRecordWaterRate(e.target.value)}
                fullWidth
              />
              <FormControl size="small" fullWidth disabled={!recordWaterRate}>
                <InputLabel id="record-water-unit-label">Unit</InputLabel>
                <Select labelId="record-water-unit-label" label="Unit" value={recordWaterRateUnitId} onChange={(e) => setRecordWaterRateUnitId(e.target.value)}>
                  {units.map((u) => (
                    <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TextField
              size="small"
              label="Notes (optional)"
              value={recordNotes}
              onChange={(e) => setRecordNotes(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />

            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Products applied (e.g. Glyphosate 450 @ 1.5 L/ha)
              </Typography>
              {recordProducts.map((row, i) => (
                <Box key={i} sx={{ mb: 1.5, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                    <ProductOrGroupAutocomplete
                      authHeader={authHeader}
                      productId={row.productId}
                      productGroupId={row.productGroupId}
                      name={row.productName}
                      onChange={(selection) =>
                        setRecordProducts((prev) =>
                          prev.map((r, idx) =>
                            idx === i
                              ? {
                                  ...r,
                                  productId: selection?.kind === 'product' ? selection.id : null,
                                  productGroupId: selection?.kind === 'group' ? selection.id : null,
                                  productName: selection?.name ?? '',
                                }
                              : r
                          )
                        )
                      }
                    />
                    <IconButton size="small" onClick={() => removeRecordProductRow(i)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      label="Application rate"
                      type="number"
                      value={row.rate}
                      onChange={(e) => setRecordProducts((prev) => prev.map((r, idx) => (idx === i ? { ...r, rate: e.target.value } : r)))}
                      helperText={isBanded(Number(recordBandPercent)) && Number(row.rate) > 0
                        ? `${formatRate(effectiveRate(Number(row.rate), Number(recordBandPercent)))} effective`
                        : undefined}
                      fullWidth
                    />
                    <FormControl size="small" fullWidth>
                      <InputLabel id={`record-product-unit-label-${i}`}>Unit</InputLabel>
                      <Select
                        labelId={`record-product-unit-label-${i}`}
                        label="Unit"
                        value={row.rateUnitId}
                        onChange={(e) => setRecordProducts((prev) => prev.map((r, idx) => (idx === i ? { ...r, rateUnitId: e.target.value } : r)))}
                      >
                        {units.map((u) => (
                          <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              ))}
              <Button size="small" startIcon={<AddIcon />} onClick={addRecordProductRow}>
                Add product
              </Button>
            </Box>
          </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelRecordDialog} disabled={recordSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRecord} disabled={recordSaving || !recordTypeId || !recordTimeFrom || recordAreaInvalid || recordBandInvalid}>
            {recordSaving ? 'Saving…' : recordEditingId ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface ManageTypesDialogProps {
  open: boolean;
  onClose: () => void;
  authHeader: { Authorization: string };
  onChanged: () => void;
}

const GEOMETRY_KINDS: GeometryKind[] = ['Polygon', 'LineString', 'Point'];

const ManageTypesDialog: React.FC<ManageTypesDialogProps> = ({ open, onClose, authHeader, onChanged }) => {
  const [types, setTypes] = useState<SpatialLayerType[]>([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [newKind, setNewKind] = useState<GeometryKind>('Polygon');
  const [newAllowedKinds, setNewAllowedKinds] = useState<GeometryKind[]>(['Polygon']);
  const [newCategory, setNewCategory] = useState<LayerCategory>('Farm');
  const [newColour, setNewColour] = useState('#4fc3f7');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/spatial-layer-types', { headers: authHeader });
      if (res.ok) setTypes(await res.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/spatial-layer-types', {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newName.trim(),
          defaultGeometryKind: newKind,
          allowedGeometryKinds: newAllowedKinds.includes(newKind) ? newAllowedKinds : [...newAllowedKinds, newKind],
          category: newCategory,
          defaultColour: newColour,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to add type');
      }
      setNewName('');
      await fetchTypes();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add type');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (id: string) => {
    setSaving(true);
    try {
      await fetch(`/api/spatial-layer-types/${id}`, { method: 'DELETE', headers: authHeader });
      await fetchTypes();
      onChanged();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Manage Layer Types</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
        {loading ? (
          <Box display="flex" justifyContent="center" py={2}><CircularProgress size={20} /></Box>
        ) : (
          <List dense>
            {types.map((t) => (
              <Box key={t.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: t.defaultColour || '#888', flexShrink: 0 }} />
                  <ListItemText primary={t.name} secondary={`${t.category} · ${t.allowedGeometryKinds.join('/')} (default ${t.defaultGeometryKind})`} />
                </Box>
                <IconButton size="small" color="error" onClick={() => handleArchive(t.id)} disabled={saving}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </List>
        )}
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" label="New type name" value={newName} onChange={(e) => setNewName(e.target.value)} fullWidth />
            <TextField
              size="small"
              label="Colour"
              type="color"
              value={newColour}
              onChange={(e) => setNewColour(e.target.value)}
              sx={{ width: 64, flexShrink: 0 }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <FormControl size="small" fullWidth>
            <InputLabel id="new-type-category-label">Category</InputLabel>
            <Select
              labelId="new-type-category-label"
              label="Category"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value as LayerCategory)}
            >
              {CATEGORY_BAR_ORDER.map((c) => <MenuItem key={c} value={c}>{CATEGORY_LABELS[c]}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel id="new-type-allowed-label">Allowed Shapes</InputLabel>
            <Select
              labelId="new-type-allowed-label"
              label="Allowed Shapes"
              multiple
              value={newAllowedKinds}
              onChange={(e) => {
                const value = e.target.value;
                const kinds = (typeof value === 'string' ? value.split(',') : value) as GeometryKind[];
                setNewAllowedKinds(kinds);
                if (kinds.length > 0 && !kinds.includes(newKind)) {
                  setNewKind(kinds[0]);
                }
              }}
              renderValue={(selected) => (selected as GeometryKind[]).join(', ')}
            >
              {GEOMETRY_KINDS.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel id="new-type-kind-label">Default Shape</InputLabel>
            <Select
              labelId="new-type-kind-label"
              label="Default Shape"
              value={newKind}
              onChange={(e) => setNewKind(e.target.value as GeometryKind)}
            >
              {(newAllowedKinds.length > 0 ? newAllowedKinds : GEOMETRY_KINDS).map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" size="small" onClick={handleAdd} disabled={saving || !newName.trim()}>Add Type</Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessMapTab;
